import { createReducer, current } from '@reduxjs/toolkit';
import type { TDOId } from '@common-modules/universal/models/Brands';
import * as actions from '../actions';
import {
  applyPiiMatches,
  mergePiiResultsIntoDetections,
  buildPersistedRedactionState,
  snapshotFromSession,
  watermarkImageAssetIds,
  restoreWatermarkImageAssetIds,
  pushUndoSnapshot,
  popToUndo,
  popToRedo,
  toggleDetection,
  deriveGroupCheckedState,
  deriveDetectionRedaction,
  liveMatchIdsOf,
  hasMatchIds,
  setDetectionRedacted,
  keepRedactedMatches,
  resplitTextSelectionsAfterDelete,
} from '../helpers';
import {
  defaultState,
  emptyIntegrationsState,
  ALL_PII_TYPES,
  DIRECT_PII_TYPES,
  INDIRECT_PII_TYPES,
} from '../store';
import {
  AppliedLexiconRecord,
  Detection,
  isLexiconDetection,
  clampWatermarkOpacity,
  DocumentComment,
  DocumentsStore,
  isSessionWorthKeeping,
  LexiconApplyState,
  PiiTypeId,
  Redaction,
  SessionState,
  UndoSnapshot,
} from '../models';

/**
 * Compute the next `Region N` integer not already used by any
 * region (manual / polygon) redaction in `existing`. Parses each
 * name with the `Region (\d+)` pattern and returns `max + 1`,
 * defaulting to 1 when nothing matches. Used by CREATE_MANUAL_*
 * reducers so new regions land with a unique default name even
 * after deletes.
 */
function nextRegionNumber(existing: Redaction[]): number {
  let max = 0;
  for (const r of existing) {
    if (r.origin !== 'manual') {
      continue;
    }
    const m = r.name?.match(/^Region (\d+)$/);
    if (m) {
      const n = parseInt(m[1]!, 10);
      if (n > max) {
        max = n;
      }
    }
  }
  return max + 1;
}

/**
 * Stamp `name: 'Region N'` onto every region-origin redaction in
 * `incoming` that doesn't already have a name. N keeps incrementing
 * across the batch so bulk creates (e.g. polygon-from-text) get a
 * distinct name per entry.
 */
function withDefaultRegionNames(
  existing: Redaction[],
  incoming: Redaction[]
): Redaction[] {
  let n = nextRegionNumber(existing);
  return incoming.map((r) => {
    if (r.origin === 'manual' && (!r.name || !r.name.trim())) {
      const named = { ...r, name: `Region ${n}` };
      n += 1;
      return named;
    }
    return r;
  });
}

type Geom = Redaction['geometry'];

/** Axis-aligned box intersection test (touching edges don't count). */
function rectsOverlap(a: Geom, b: Geom): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

/** Smallest box containing both inputs. */
function unionBox(a: Geom, b: Geom): Geom {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const right = Math.max(a.x + a.width, b.x + b.width);
  const bottom = Math.max(a.y + a.height, b.y + b.height);
  return { x, y, width: right - x, height: bottom - y };
}

/**
 * Reducer for the documents slice.
 *
 * Scope of this first pass: cover the core editor mutations so the UI can
 * exercise OPEN, view/tool changes, detection toggling, manual redactions,
 * undo/redo, save status, and lock state. Less-frequent paths (push outcomes,
 * version history) are left as no-ops with `// TODO` markers and will be
 * filled in alongside their saga counterparts.
 */

/**
 * Clear the redaction/PII selection whenever a keyword search runs. Added when
 * search highlights were yellow (a selected box is also yellow, so they were
 * hard to tell apart). Disabled now that search highlights are blue — the
 * colors no longer collide, and clearing selection mid-search was disruptive.
 * Flip to `true` to restore the behavior.
 */
const DESELECT_ON_SEARCH = false;

/**
 * Has anyone done redaction work on this document yet?
 *
 * Decides which tab the editor opens on (see `redactionsSubtab` below).
 * "Work" is anything the reviewer would want to come back to: a saved
 * redaction, a whole page marked or removed, or a detection already promoted
 * to the Redactions list. Applied lexicons and PII results are deliberately
 * NOT work — they are findings nobody has acted on, and a document full of
 * untouched findings is exactly the one that wants the Detections tab.
 */
export const hasRedactionWork = (
  savedState:
    | {
        redactions?: readonly unknown[];
        pageRedacts?: readonly unknown[];
        excludedPages?: readonly unknown[];
      }
    | null
    | undefined,
  ...detectionLists: readonly (readonly Detection[])[]
): boolean =>
  (savedState?.redactions?.length ?? 0) > 0 ||
  (savedState?.pageRedacts?.length ?? 0) > 0 ||
  (savedState?.excludedPages?.length ?? 0) > 0 ||
  detectionLists.some((list) => list.some((d) => d.addedToRedactions));

/**
 * Making a redaction by hand — drawing it, selecting text, keywording it, or
 * marking a whole page. Any of these means the reviewer has started producing
 * work, so the Redactions tab is now the one worth looking at.
 *
 * Detection toggles are NOT here on purpose. Promoting a PII or lexicon match
 * is work done FROM the Detections tab, and yanking the panel away from the
 * list someone is working down is the opposite of helpful.
 */
const PRODUCES_REDACTION_WORK: ReadonlySet<string> = new Set([
  actions.CREATE_MANUAL_REDACTION.type,
  actions.CREATE_MANUAL_REDACTIONS.type,
  actions.CREATE_OR_MERGE_TEXT_REDACTIONS.type,
  actions.TOGGLE_PAGE_REDACT.type,
  actions.TOGGLE_PAGE_EXCLUDE.type,
]);

/**
 * Working in the right panel, which ends the editor's licence to move it.
 *
 * Broad on purpose: an explicit tab click is the obvious case, but expanding
 * a card, filtering, searching, promoting a detection or starting the engine
 * are all someone using this panel and expecting it to stay put.
 */
const ENDS_PANEL_HANDOVER: ReadonlySet<string> = new Set([
  actions.SET_DETECTIONS_TAB.type,
  actions.SET_REDACTIONS_SUBTAB.type,
  actions.TOGGLE_DETECTION.type,
  actions.SET_DETECTIONS_REDACTED.type,
  actions.TOGGLE_DETECTION_PAGE.type,
  actions.TOGGLE_DETECTION_MATCH.type,
  actions.TOGGLE_DETECTION_EXPANDED.type,
  actions.DELETE_DETECTION_MATCH.type,
  actions.REMOVE_DETECTIONS_FROM_REDACTIONS.type,
  actions.SELECT_DETECTION_ROW.type,
  actions.SELECT_DETECTION_ROWS.type,
  actions.SET_DETECTION_FILTER.type,
  actions.SET_DETECTION_SEARCH.type,
  actions.SET_LEXICON_FILTER.type,
  actions.SET_ALL_LEXICON_FILTERS.type,
  actions.SET_DETECTION_SOURCE.type,
  actions.RUN_PII_DETECTION.type,
  actions.APPLY_LEXICONS_TO_OPEN_DOCUMENT.type,
  actions.REMOVE_LEXICON_FROM_DOCUMENT.type,
]);

/**
 * VE-24947 — park the active document in `suspendedSessions` if it has work
 * worth keeping, then clear the active slot.
 *
 * DIRTY ONLY, and this is the single place that rule lives. `hasUnsavedWork`
 * covers `error` as well as `dirty`, so a save that FAILED is kept too — that
 * is the case where losing it matters most, since nothing else on screen is
 * going to rescue those edits. A CLEAN document is dropped: re-opening it from
 * the server reproduces it exactly, and keeping every file the reviewer glanced
 * at resident is the unbounded-memory failure NFR1 warns about.
 */
/**
 * The session an outcome belongs to — on screen OR suspended.
 *
 * Saves and burn-ins both outlive a document switch (and a burn-in outlives
 * the editor entirely, VE-28061), so by the time either reports back its
 * document may be parked in `suspendedSessions`. Every one of these cases used
 * to test `state.session?.tdoId` alone and return otherwise, written when "not
 * the current session" could only mean "editor closed, nothing left to
 * update". With switching (VE-24947) it also means "parked", and the outcome
 * was dropped on the floor.
 *
 * For burn-ins that meant the finished file never reached that document's
 * versionHistory and its `export.phase` stayed mid-flight forever — which
 * `selectIsExporting` reads, so Redact File came back disabled for the rest of
 * the session. For saves it was worse, because Save All is the whole reason
 * suspended sessions emit save actions at all: the asset reached the server
 * while the session stayed `dirty`, so the panel kept its unsaved marker and
 * the leave guard kept offering "Discard and leave" for work already saved —
 * teaching reviewers to click through the one prompt that protects real
 * unsaved redactions.
 *
 * Undefined still means genuinely gone (closed, or never opened here), which
 * every caller must keep treating as a no-op.
 */
const sessionFor = (state: DocumentsStore, tdoId: TDOId) => {
  if (state.session?.tdoId === tdoId) {
    return state.session;
  }
  return state.suspendedSessions[tdoId];
};

const suspendIfWorthKeeping = (state: DocumentsStore) => {
  const session = state.session;
  if (session && isSessionWorthKeeping(session.save)) {
    state.suspendedSessions[session.tdoId] = session;
  }
  state.session = null;
};

const reducer = createReducer<DocumentsStore>(defaultState, (builder) => {
  builder
    // --- Lifecycle -------------------------------------------------------
    .addCase(actions.OPEN_DOCUMENT, (state) => {
      // Clear stale failure state so the editor doesn't render the error UI
      // while the new open is in flight.
      state.loadError = null;
      // VE-21982: drop any comments left over from a previous document so
      // the panel doesn't briefly flash stale rows while the new fetch is
      // in flight.
      state.documentComments = {};
      state.documentCommentDrafts = {};
      state.selectedDocumentComments = [];
      state.isFetchingDocumentComments = false;
      state.editingDocumentCommentId = null;
      state.focusedDocumentCommentId = null;
      // VE-24447 / VE-24693: a preview belongs to one open panel on one
      // document.
      state.batesPreview = null;
      state.watermarkPreview = null;
      // VE-23442: drop any "Send to" send/dialog state (and the cached
      // external-integration config) left over from a previous document.
      state.integrations = emptyIntegrationsState();
    })
    .addCase(actions.OPEN_DOCUMENT_SUCCESS, (state, action) => {
      // Belongs to the document being left. Carrying it across a switch would
      // announce one file's redaction over another file's editor.
      state.redactionCompleteNotice = null;
      const {
        tdoId,
        documentName,
        pdfUrl,
        mediaAssetId,
        mediaAssetEngineId,
        totalPages,
        detections,
        savedState,
      } = action.payload;
      state.loadError = null;
      // The journey is over however it ends, including the restore path below.
      // The tab it was heading for outlives it by one step: a switch made from
      // the Redacted File tab means "show me THAT file's redacted output", and
      // both paths below would otherwise land on the editor — a fresh session
      // starts there, and a restored one remembers wherever it was last left.
      const landOnTab = state.pendingDocument?.activeTab ?? null;
      state.pendingDocument = null;
      // VE-24947: park the document being replaced before installing this one,
      // so a switch never costs the reviewer unsaved work. A cold open has no
      // outgoing session and this is a no-op. Re-opening the SAME document
      // (trim, view-redacted-file) would suspend and immediately restore it,
      // which the guard below prevents by leaving `session` alone in that case.
      if (state.session && state.session.tdoId !== tdoId) {
        suspendIfWorthKeeping(state);
      }

      // VE-24947: this document was suspended with unsaved work, so restore
      // THAT and drop what the server just returned.
      //
      // The reviewer's in-progress edits win over the saved state on purpose —
      // keeping them is the entire point of suspending. The cost is real and
      // worth naming: anything that changed server-side while they were away
      // (a finished PII engine run, a colleague's save) is not picked up on the
      // way back. That is the same exposure the editor already has for a single
      // long-lived session, which is what the staleness check (`staleness.ts`)
      // and the TDO lock exist to bound — not a new one this creates.
      const suspended = state.suspendedSessions[tdoId];
      if (suspended) {
        delete state.suspendedSessions[tdoId];
        state.session = suspended;
        if (landOnTab) {
          state.session.activeTab = landOnTab;
        }
        // THE FILE IS RE-RESOLVED; THE WORK IS NOT.
        //
        // Everything above this line is the reviewer's — redactions, undo
        // stack, selections, unsaved or not — and it comes back untouched.
        // What does NOT come back is the parked session's idea of which bytes
        // to render: `pdfUrl` was signed from whatever was primary when they
        // left, and OCR republishes its searchable copy AS the new primary. A
        // document parked mid-OCR therefore came back rendering a file with no
        // text layer while the UI reported OCR complete over it (BL-5).
        //
        // Adopting the fresh asset is safe for the redactions already drawn
        // because the enriched copy keeps the original's page geometry — the
        // engine adds a text layer and touches no page box, which is the same
        // invariant `RELOAD_FOR_OCR` relies on. It is also what the two
        // pre-passes were ALREADY doing: both index `payload.pdfUrl`, so
        // before this the text index and the rendered page disagreed about
        // which file the document was.
        //
        // No save and no re-open: re-opening rebuilds from the persisted
        // asset, which would discard exactly the unsaved work parking exists
        // to keep. Swapping the url in place keeps both.
        //
        // An empty `pdfUrl` means the media lookup came back with nothing;
        // keep what the session had rather than blanking a document that was
        // rendering fine.
        if (pdfUrl) {
          state.session.pdfUrl = pdfUrl;
          state.session.mediaAssetId = mediaAssetId ?? null;
          state.session.mediaAssetEngineId = mediaAssetEngineId ?? null;
        }
        return;
      }

      // Persisted PII selections (per-match records). Stashed in session so
      // they can be re-applied to engine detections that arrive later via
      // PII_RESULTS_LOADED.
      const piiMatches = action.payload.piiMatches ?? [];

      // Base detection state — engine output initialised as unchecked — then
      // re-apply persisted selections by stable vendor match id.
      const detectionList: Detection[] = applyPiiMatches(
        detections.map((d) => ({
          ...d,
          checked: false,
          expanded: false,
        })),
        piiMatches
      );

      // Lexicon detections (VE-24948 / U5) come back WHOLE from the asset —
      // geometry, snapshot and triage state — so nothing here consults a
      // lexicon. Appended after the PII rows rather than interleaved: the panel
      // orders its own list, and appending keeps existing PII ordering (and
      // every snapshot test of it) untouched.
      const restoredLexiconDetections = savedState?.lexiconDetections ?? [];
      // Which tab this document opens on, and whether the editor is still
      // allowed to change its mind — see `autoLandedOnDetections`.
      const landedOnDetections = !hasRedactionWork(
        savedState,
        detectionList,
        restoredLexiconDetections
      );

      const session: SessionState = {
        tdoId,
        documentName,
        pdfUrl,
        mediaAssetId: mediaAssetId ?? null,
        mediaAssetEngineId: mediaAssetEngineId ?? null,
        totalPages,
        // Results present → 'done'; otherwise 'checking' — the shared
        // engine-status poller (sagas/engineStatus.ts) reads the server for a
        // running job / existing results and resolves this to processing / done
        // / not-run. Starting at 'checking' (not 'not-run') keeps the subtab
        // from flashing "hasn't been run" while that check is in flight
        // (VE-27250).
        // `viewSeed.piiState` wins on an OCR reload: the reviewer watched
        // these results arrive, and `checking` there reads as the work being
        // redone. Falls back to the normal open rule — detections already on
        // the saved asset mean done, otherwise the poller resolves it.
        piiEngineState:
          action.payload.viewSeed?.piiState ??
          (detectionList.length > 0 ? 'done' : 'checking'),
        // Resolved by the shared engine-status poller: 'done' with a manifest,
        // 'not-run' when this TDO has no OCR task, 'failed', or 'processing'
        // while OCR is still reading. Starts at 'checking' so no OCR messaging
        // renders before the answer is known.
        ocrEngineState: 'checking',
        ocrManifest: null,
        showOcrRegions: false,
        // Seeded on an OCR reload so the reviewer lands where they were.
        currentPage: action.payload.viewSeed?.page ?? 1,
        // CARRIED FORWARD AND BUMPED, not reset to 1. The canvas scrolls on a
        // CHANGE to this token, and the session it is replacing may already
        // have been at 1 — in which case a fresh 1 is no change at all and the
        // seeded page is set without the viewport ever moving. That is why the
        // reload still landed at the top.
        scrollToken:
          action.payload.viewSeed?.page && action.payload.viewSeed.page > 1
            ? (state.session?.scrollToken ?? 0) + 1
            : 0,
        scrollToY: null,
        zoom: action.payload.viewSeed?.zoom ?? 100,
        // 'initial' = fit-width, capped to native size on wide screens (see
        // computePageWidth). Flips to a real mode on first user zoom action.
        zoomMode: 'initial',
        activeTool: 'select',
        activeTab: 'editor',
        detectionsTab: 'detections',
        /**
         * LAND ON DETECTIONS WHEN THERE IS NO REDACTION WORK TO COME BACK TO.
         *
         * `redactionsSubtab: 'pii'` is what the top tab reads as Detections
         * (see `effectiveDetectionsTopTab` — the durable values predate both
         * labels). 'all' means the Redactions tab, and on a document nobody
         * has touched that tab is an empty list with nothing to do from it,
         * while Detections carries both ways of producing something: Run PII
         * Detection and the lexicon control.
         *
         * "Untouched" is measured against work the reviewer would want to
         * return to — saved redactions, whole pages marked or removed, or a
         * detection already promoted. Any of those and the Redactions tab
         * wins, because it is where that work lives.
         *
         * Note this also turns on candidate-PII painting on the canvas, which
         * `DetectionMaskOverlay` gates on the same value. That is the right
         * pairing: it is the tab's own behaviour, and a document with no
         * detections yet has no candidates to paint.
         */
        redactionsSubtab: landedOnDetections ? 'pii' : 'all',
        // Only when the editor CHOSE Detections. A document opened on
        // Redactions because it already has work was not landed anywhere —
        // there is nothing to hand back.
        autoLandedOnDetections: landedOnDetections,
        detections: [...detectionList, ...restoredLexiconDetections],
        appliedLexicons: savedState?.appliedLexicons ?? [],
        lexiconApply: emptyLexiconApplyState(),
        persistedPiiMatches: piiMatches,
        redactions: savedState?.redactions ?? [],
        detectionRectOverrides: savedState?.detectionRectOverrides ?? {},
        pageRedacts: savedState?.pageRedacts ?? [],
        excludedPages: savedState?.excludedPages ?? [],
        selectedRedactionIds: [],
        selectedDetectionRowKeys: [],
        selectedDetectionMaskKey: null,
        // Empty on an ordinary open; the OCR reload seeds it so the search the
        // reviewer was running survives the rebuild. `matches` always starts
        // empty — the pre-pass re-run recomputes them.
        keywordSearch: {
          phrase: action.payload.keywordSearchSeed?.phrase ?? '',
          matches: [],
          lastSearchedAt: 0,
          matchCase: action.payload.keywordSearchSeed?.matchCase ?? false,
          wholeWord: action.payload.keywordSearchSeed?.wholeWord ?? false,
        },
        detectionSearch: '',
        detectionFilter: {
          direct: [...DIRECT_PII_TYPES],
          indirect: [...INDIRECT_PII_TYPES],
        },
        // Exceptions only, so a lexicon applied later is visible by default.
        lexiconFilterUnchecked: [],
        detectionSource: 'all',
        undoStack: { past: [], future: [] },
        save: {
          status: 'clean',
          lastSavedAt: action.payload.lastSavedAt ?? null,
          rev: 0,
          lastSavedBurnInVersion: action.payload.lastSavedBurnInVersion,
        },
        export: { phase: 'idle' },
        watermarks: savedState?.watermarks ?? [],
        watermarksOff: savedState?.watermarksOff ?? false,
        // Empty because the restore pass has not run yet; it reports into this
        // a moment later. Reset per open, like everything else here.
        watermarkImageRestoreFailures: [],
        batesStamp: savedState?.batesStamp ?? null,
        batesStampOff: savedState?.batesStampOff ?? null,
        watermarkEditing: false,
        selectedWatermarkId: null,
        redactionStatus: action.payload.redactionStatus ?? null,
        redactionStatusName: action.payload.redactionStatusName ?? null,
        versionHistory: action.payload.versionHistory ?? [],
        // Lazily filled when Side-by-Side needs a version's real deleted pages.
        redactedExcludedPagesByVersion: {},
        vendorRequestIds: action.payload.vendorRequestIds ?? {
          govQa: null,
          foiaXpress: null,
          casepoint: null,
          exterro: null,
          nuix: null,
        },
        folderId: action.payload.folderId ?? null,
        folderName: action.payload.folderName ?? null,
        pageTexts: {},
        pageTextItems: {},
        pageSearchability: {},
        pageDimensions: {},
        ocrWarningDismissed: false,
        ocrProcessedDismissed: false,
        // Pre-pass hasn't indexed the document yet — keyword search covers only
        // render-path pages until PDF_TEXT_PREPASS_DONE flips this.
        textIndexReady: false,
        textIndexProgress: null,
        // The scan that decides which pages need OCR has not run yet, so
        // nothing may read `pageSearchability` as an answer about this
        // document — see `searchabilityScanReady`.
        searchabilityScanReady: false,
        searchabilityScanProgress: null,
      };
      state.session = session;
      if (landOnTab) {
        state.session.activeTab = landOnTab;
      }
    })
    .addCase(actions.SUSPEND_ACTIVE_DOCUMENT, (state) => {
      suspendIfWorthKeeping(state);
    })
    // The reviewer took the OCR reload. Raised HERE rather than when the
    // re-evaluation starts: that only happens once the new session's pre-pass
    // has extracted text, and the panel's numbers are stale for the whole gap
    // in between. Only when there is something to re-run — a document with no
    // lexicons has nothing to say about them.
    .addCase(actions.RELOAD_FOR_OCR, (state) => {
      state.ocrReloadInProgress =
        (state.session?.appliedLexicons.length ?? 0) > 0;
    })
    // Whatever the reload was going to do about lexicons, it is done — see
    // `OCR_RELOAD_SETTLED` for why this cannot be inferred from any one of the
    // lifecycle actions.
    .addCase(actions.OCR_RELOAD_SETTLED, (state) => {
      state.ocrReloadInProgress = false;
    })
    .addCase(actions.SWITCH_DOCUMENT_STARTED, (state, action) => {
      // Records the destination only. `session` deliberately keeps the OUTGOING
      // document so the editor's chrome stays mounted while the next one loads;
      // OPEN_DOCUMENT_SUCCESS does the swap in one step.
      state.pendingDocument = action.payload;
    })
    .addCase(actions.OPEN_DOCUMENT_FAILURE, (state, action) => {
      state.ocrReloadInProgress = false;
      /**
       * A failed SWITCH leaves the reviewer where they were.
       *
       * This used to null the session unconditionally — the one session-ending
       * path that did not park unsaved work first. Since a switch deliberately
       * keeps the outgoing document as `session` until its replacement lands
       * (that is what keeps the chrome on screen), a failed open destroyed
       * whatever the reviewer had redacted but not saved: no prompt, no
       * marker, no snackbar, and a full-page error where a working editor had
       * been.
       *
       * Parking it would be enough to stop the loss, but the better answer is
       * that there is nothing to park. The document on screen is untouched and
       * fully loaded; only the fetch for a DIFFERENT file failed. So the
       * switch is simply abandoned and the reviewer carries on. The saga says
       * what went wrong (a snackbar naming the file that would not open).
       */
      const wasSwitching = state.pendingDocument !== null;
      state.pendingDocument = null;
      if (wasSwitching && state.session) {
        return;
      }
      // An INITIAL open failing is different: there is no working editor to
      // keep, so the error screen is right. Park anything worth keeping first
      // — this path should never hold the last copy of a reviewer's edits.
      suspendIfWorthKeeping(state);
      state.loadError = action.payload.message;
    })
    .addCase(actions.HYDRATE_PANEL_PREFERENCES, (state, action) => {
      state.documentsPanelOpen = action.payload.documentsPanelOpen;
      // Restored, not requested: the panel comes back as the strip the
      // reviewer left, not at full width over the page rail. See
      // `DocumentsStore.documentsPanelExpanded`.
      state.documentsPanelExpanded = false;
      state.documentsPanelPinned = action.payload.documentsPanelPinned;
      state.pagesPanelCollapsed = action.payload.pagesPanelCollapsed;
      // Unlike `documentsPanelExpanded` above, this restores verbatim. Nothing
      // about a wide tool rail covers anything or asks for a grace period — it
      // is simply how wide the rail was on this document last time.
      state.toolPanelExpanded = action.payload.toolPanelExpanded;
    })
    .addCase(actions.SET_TOOL_PANEL_EXPANDED, (state, action) => {
      state.toolPanelExpanded = action.payload;
    })
    .addCase(actions.SET_DOCUMENTS_PANEL_PINNED, (state, action) => {
      state.documentsPanelPinned = action.payload;
    })
    .addCase(actions.SAVE_ALL_DOCUMENTS, (state) => {
      state.saveAllInProgress = true;
    })
    .addCase(actions.SAVE_ALL_SETTLED, (state) => {
      state.saveAllInProgress = false;
    })
    .addCase(actions.SET_DOCUMENT_ORDER, (state, action) => {
      state.documentOrder = action.payload;
    })
    .addCase(actions.SET_PAGES_PANEL_COLLAPSED, (state, action) => {
      state.pagesPanelCollapsed = action.payload;
    })
    .addCase(actions.SET_DOCUMENTS_PANEL_OPEN, (state, action) => {
      // Editor chrome: not undoable, doesn't mark the doc dirty, and not reset
      // by CLOSE_DOCUMENT — see `DocumentsStore.documentsPanelOpen`.
      state.documentsPanelOpen = action.payload;
      // Opening it is a request to read it, so it arrives at full width.
      state.documentsPanelExpanded = action.payload;
    })

    .addCase(actions.AUTO_OPEN_DOCUMENTS_PANEL, (state) => {
      // Deliberately not folded into SET_DOCUMENTS_PANEL_OPEN: the preferences
      // saga watches that action, and an open the editor performed for the
      // reviewer must not be written down as the reviewer's choice.
      state.documentsPanelOpen = true;
      // Expanded, unlike a restore: this open exists to offer the reviewer a
      // document to switch to, which they cannot do from the strip.
      state.documentsPanelExpanded = true;
    })
    .addCase(actions.CLOSE_DOCUMENT, (state) => {
      state.session = null;
      state.redactionCompleteNotice = null;
      // Nothing to reload into. Cleared here and at OPEN_DOCUMENT_FAILURE so
      // the notice cannot outlive the document it was about.
      state.ocrReloadInProgress = false;
      // VE-24947: leaving the editor ends the whole multi-document session, so
      // every suspended document goes with it. Switching documents parks the
      // outgoing one inside OPEN_DOCUMENT_SUCCESS instead, precisely so it
      // does NOT land here (see `SUSPEND_ACTIVE_DOCUMENT`, which is a
      // test-only lever and never dispatched in app code).
      // Anything unsaved in this map at this point was discarded deliberately —
      // the close path is what warns about it (see DocumentEditor's onClose).
      state.suspendedSessions = {};
      // Same lifetime as the suspended sessions above: both describe one
      // editing session, which ends here.
      state.burnInsStartedHere = {};
      state.pendingDocument = null;
      // The ordering belongs to the editing session, which ends here.
      state.documentOrder = null;
      state.saveAllInProgress = false;
      state.loadError = null;
      state.documentComments = {};
      state.documentCommentDrafts = {};
      state.selectedDocumentComments = [];
      state.isFetchingDocumentComments = false;
      state.editingDocumentCommentId = null;
      state.focusedDocumentCommentId = null;
      state.batesPreview = null;
      state.watermarkPreview = null;
      state.integrations = emptyIntegrationsState();
    })

    // --- Custom lexicons (VE-24948 / U5) ---------------------------------
    //
    // Upload selection is slice-level (no session exists at upload). BR-49:
    // recorded, never evaluated here — evaluation happens at first editor open.
    .addCase(actions.SELECT_LEXICONS_FOR_UPLOAD, (state, action) => {
      state.uploadLexiconSelection = [...new Set(action.payload.lexiconIds)];
      // NOT intersected with the selection. Auto-redact is a per-lexicon
      // PREFERENCE, remembered whether or not the lexicon is currently ticked
      // — Auto Redact All turns every row on without selecting anything, and a
      // row switched on and then unticked must come back on when it is ticked
      // again. The intersection happens at STAGING, where it decides what is
      // actually recorded.
      state.uploadLexiconAutoRedact = [
        ...new Set(action.payload.autoRedactLexiconIds ?? []),
      ];
    })
    /**
     * Snapshot the selection against one batch's files — see the actions and
     * `DocumentsStore.uploadLexiconBatch` for why the batch, not the live
     * selection, is what recording consumes.
     *
     * The live selection SURVIVES. It used to be cleared here and again on
     * every mount of the upload selector, which made lexicons the one
     * ingestion option that did not carry from one upload to the next — every
     * other checkbox in that dialog does, because this store outlives the
     * dialog, and that is the intended behaviour rather than an accident of
     * the slice's lifetime.
     *
     * Nothing double-records as a result: the batch holds its own copy and is
     * drained file by file (`CONSUME_UPLOAD_LEXICON_BATCH_FILE`), and the
     * live selection has exactly one reader, the selector component itself.
     */
    .addCase(actions.STAGE_UPLOAD_LEXICON_BATCH, (state, action) => {
      state.uploadLexiconBatch =
        state.uploadLexiconSelection.length > 0 &&
        action.payload.fileUrls.length > 0
          ? {
              lexiconIds: [...state.uploadLexiconSelection],
              // Intersected HERE: a lexicon that is not being applied cannot
              // be auto-redacting, and the batch is what recording reads.
              autoRedactLexiconIds: state.uploadLexiconAutoRedact.filter((id) =>
                state.uploadLexiconSelection.includes(id)
              ),
              fileUrls: [...action.payload.fileUrls],
            }
          : null;
    })
    .addCase(actions.CONSUME_UPLOAD_LEXICON_BATCH_FILE, (state, action) => {
      const batch = state.uploadLexiconBatch;
      if (!batch) {
        return;
      }
      // Remove ONE instance: a batch may legitimately contain the same URL
      // twice only if two files uploaded to the same object, and consuming
      // both at once would starve the second recording.
      const idx = batch.fileUrls.indexOf(action.payload.fileUrl);
      if (idx >= 0) {
        batch.fileUrls.splice(idx, 1);
      }
      if (batch.fileUrls.length === 0) {
        state.uploadLexiconBatch = null;
      }
    })

    //
    // Every case guards on `requestId`. A run is asynchronous and cancellable
    // (`takeLatest`), and terminating the match worker does NOT guarantee that
    // an in-flight message is never delivered — so a stale run's outcome must
    // be identifiable and dropped rather than allowed to overwrite the newer
    // one's detections.
    .addCase(actions.LEXICON_APPLY_STARTED, (state, action) => {
      if (!state.session) {
        return;
      }
      const previous = state.session.lexiconApply;
      state.session.lexiconApply = {
        status: 'running',
        requestId: action.payload.requestId,
        pagesMatched: [],
        matchCount: 0,
        crashed: false,
        unsearchedPages: [],
        abandonedEntryIds: [],
        abandonedLexiconNames: [],
        timedOut: false,
        autoRedact: {
          running: action.payload.autoRedact === true,
          // A cancel belongs to the run it was pressed during, so it starts
          // clear — otherwise declining one document's auto-redaction would
          // silently decline the next one's.
          cancelled: false,
          // A SUMMARY STILL WAITING TO BE READ SURVIVES THIS RUN. Every open
          // produces several runs — `pendingLexiconIds` always includes the
          // accumulating `sessionAppliedLexiconIds`, and `watchPageTextArrival`
          // re-evaluates on every prepass text burst — so resetting here took
          // the dialog off the screen mid-read, and the run that replaced it
          // reported 0 because `buildAppliedRecords` had already dropped the
          // `autoRedact` flag. Matches redacted with nobody asked, and nothing
          // left on screen saying so.
          //
          // It used to survive by accident: the count was summed across every
          // card's durable marker, so the next run recomputed the same number
          // and put the dialog back. That compensation went when the count was
          // narrowed to the run, which is why it is written down here now.
          redactedCount: previous.autoRedact.acknowledged
            ? 0
            : previous.autoRedact.redactedCount,
          redactedMatchIds: previous.autoRedact.acknowledged
            ? []
            : [...previous.autoRedact.redactedMatchIds],
          acknowledged: previous.autoRedact.acknowledged,
        },
        // Accumulated, never replaced: page text arriving later must be
        // re-evaluated against everything applied in this session (BR-48), not
        // just the newest selection.
        sessionAppliedLexiconIds: [
          ...new Set([
            ...previous.sessionAppliedLexiconIds,
            ...action.payload.lexiconIds,
          ]),
        ],
        // The set as of before this request — what an undo of this apply's
        // success snapshot restores (review-gate finding 4).
        //
        // Only re-captured when this request ADDS a lexicon. A re-evaluation
        // run for an already-applied set would otherwise capture a set that
        // already contains it, so undoing that run removed its new matches
        // while leaving the lexicon in the live re-evaluation set — and the
        // next page of text put them straight back (review-gate M9).
        preRequestSessionAppliedLexiconIds: action.payload.lexiconIds.some(
          (id) => !previous.sessionAppliedLexiconIds.includes(id)
        )
          ? [...previous.sessionAppliedLexiconIds]
          : [
              ...(previous.preRequestSessionAppliedLexiconIds ??
                previous.sessionAppliedLexiconIds),
            ],
      };
    })
    .addCase(actions.LEXICON_APPLY_PROGRESS, (state, action) => {
      const apply = state.session?.lexiconApply;
      if (!apply || apply.requestId !== action.payload.requestId) {
        return;
      }
      apply.pagesMatched = [
        ...new Set([...apply.pagesMatched, ...action.payload.pages]),
      ].sort((a, b) => a - b);
      apply.matchCount += action.payload.matchCount;
    })
    .addCase(actions.LEXICON_APPLY_AWAITING_TEXT, (state, action) => {
      const apply = state.session?.lexiconApply;
      if (!apply || apply.requestId !== action.payload.requestId) {
        return;
      }
      apply.status = 'awaiting-text';
    })
    .addCase(actions.LEXICON_APPLY_SUCCESS, (state, action) => {
      // Whatever else this run was, it has reported — so the reload's notice
      // has nothing left to wait for.
      state.ocrReloadInProgress = false;
      if (!state.session) {
        return;
      }
      const apply = state.session.lexiconApply;
      if (apply.requestId !== action.payload.requestId) {
        return;
      }
      // Snapshot BEFORE the merge lands so Cmd/Ctrl+Z reverses an apply as one
      // step. `mergeLexiconMatches` is additive (M1), so undo is the only way
      // back out of an apply the reviewer did not want.
      //
      // Only when something was actually added: a no-op re-evaluation pushing a
      // snapshot identical to current state makes Ctrl+Z do nothing for as many
      // presses as there were re-evals, and evicts real snapshots at
      // UNDO_STACK_LIMIT (review finding 7).
      //
      // The snapshot's re-evaluation set is the PRE-request one captured at
      // LEXICON_APPLY_STARTED: the live set already contains this apply's ids,
      // and snapshotting those would make the undo restore them — so the next
      // page-text arrival would silently re-apply what Ctrl+Z had just removed
      // (review-gate finding 4).
      if (action.payload.changed) {
        pushSnapshot(state.session, {
          sessionAppliedLexiconIds: [
            ...(apply.preRequestSessionAppliedLexiconIds ??
              apply.sessionAppliedLexiconIds),
          ],
          // This apply's own mark: undoing THIS step retires THIS run's
          // summary, and no other snapshot can (review-gate R5).
          lexiconApplyRequestId: action.payload.requestId,
        });
      }
      state.session.detections = [...action.payload.detections];
      state.session.appliedLexicons = mergeAppliedLexiconRecords(
        state.session.appliedLexicons,
        action.payload.appliedLexicons
      );
      apply.status = 'done';
      apply.matchCount = action.payload.matchCount;
      if (action.payload.disposition) {
        apply.disposition = { ...action.payload.disposition };
      } else {
        delete apply.disposition;
      }
      apply.abandonedEntryIds = [...action.payload.abandonedEntryIds];
      // Cleared, not merged, when a clean run reports none: the sentence beside
      // the count has to be able to go away, for the same reason the count does.
      apply.abandonedLexiconNames = [
        ...(action.payload.abandonedLexiconNames ?? []),
      ];
      apply.timedOut = action.payload.timedOut;
      // A dead worker is not a slow one (review-gate #3 BR-12), and a page
      // nobody searched is the fact a reviewer can act on (BR-11).
      apply.crashed = action.payload.crashed ?? false;
      apply.unsearchedPages = [...(action.payload.unsearchedPages ?? [])];
      apply.indexing = action.payload.indexing ?? false;
      apply.pageCountUnknown = action.payload.pageCountUnknown ?? false;
      apply.indexFailed = action.payload.indexFailed ?? false;
      apply.pagesMatched = [
        ...new Set([...apply.pagesMatched, ...action.payload.pagesMatched]),
      ].sort((a, b) => a - b);
      delete apply.errorMessage;
      // The auto-redact half settles here, and `acknowledged` is what decides
      // whether a dialog appears at all: a run that redacted nothing has
      // nothing to say, and a dialog saying so is a click for nothing.
      apply.autoRedact.running = false;
      // ACCUMULATED, not overwritten, and only ever settled by a run that had
      // something to settle. A neutral re-evaluation — the ordinary background
      // run that follows every page-text burst — reports 0, and letting that 0
      // mark an unread summary acknowledged is how a real run's report
      // disappeared a second after it arrived.
      const runCount = action.payload.autoRedactedCount ?? 0;
      if (runCount > 0) {
        apply.autoRedact.redactedCount += runCount;
        apply.autoRedact.redactedMatchIds.push(
          ...(action.payload.autoRedactedMatchIds ?? [])
        );
        apply.autoRedact.acknowledged = false;
      }
      // ALWAYS dirty, unlike the snapshot above.
      //
      // `changed` answers "is this worth an undo step and an audit event" — it
      // must not also decide "is this worth saving". A run that adds no matches
      // still replaces `detections` and merges `appliedLexicons` (resolving
      // `evaluatedAt` from null, which is what stops it re-running forever), and
      // gating the dirty flag on `changed` meant that run never reached
      // autosave. The asset then carried `appliedLexicons` and NO
      // `lexiconDetections` — so the next open restored a document that claimed
      // a lexicon was applied and listed none of its matches, which is the
      // "already applied, 0 in this list" state exactly.
      markDirty(state.session);
    })
    .addCase(actions.CANCEL_LEXICON_AUTO_REDACT, (state) => {
      if (!state.session) {
        return;
      }
      // The SEARCH keeps going — see the action. The saga re-reads this flag
      // when the matches land, so a cancel pressed at any point before then
      // still takes effect, and one pressed after does nothing rather than
      // half-undoing a finished run.
      state.session.lexiconApply.autoRedact.cancelled = true;
    })
    .addCase(actions.ACKNOWLEDGE_LEXICON_AUTO_REDACT, (state) => {
      if (!state.session) {
        return;
      }
      state.session.lexiconApply.autoRedact.acknowledged = true;
    })
    .addCase(actions.REMOVE_LEXICON_AUTO_REDACTIONS, (state) => {
      if (!state.session) {
        return;
      }
      // ONLY WHAT THE DIALOG REPORTED. The card's `autoRedactedMatchIds` is
      // durable — it survives an un-redact, a lexicon removal and a reopen,
      // because it records how a match first came to be redacted. Walking it
      // meant a dialog saying "3 redactions have been automatically applied"
      // could take back 43, including matches the reviewer accepted in an
      // earlier session and would have had to notice to recover.
      //
      // The run's own ids are recorded on `lexiconApply.autoRedact` beside the
      // count, and accumulate with it, so the button always undoes exactly the
      // matches the number is describing.
      const reported = new Set(
        state.session.lexiconApply.autoRedact.redactedMatchIds
      );
      pushSnapshot(state.session);
      for (const detection of state.session.detections) {
        const auto = detection.autoRedactedMatchIds;
        if (!auto?.length) {
          continue;
        }
        // ONLY the automatic ones. A reviewer who redacted something by hand
        // before pressing this keeps it — which is the whole reason the two
        // lists are recorded separately.
        const automatic = new Set(auto.filter((id) => reported.has(id)));
        if (automatic.size === 0) {
          continue;
        }
        const kept = (detection.redactedMatchIds ?? []).filter(
          (id) => !automatic.has(id)
        );
        const derived = deriveDetectionRedaction(detection, kept);
        detection.redactedMatchIds = derived.redactedMatchIds;
        detection.checkedPages = derived.checkedPages;
        detection.checked = derived.checked;
        // The marker stays. It records that these matches WERE auto-redacted,
        // which remains true — and a reviewer who re-redacts one by hand has
        // still not reviewed it any less than they did a moment ago.
        if (derived.redactedMatchIds.length === 0) {
          // Nothing of this card is redacted any more, so it is a suggestion
          // again rather than work — the same rule the un-redact path follows.
          delete detection.addedToRedactions;
        }
      }
      state.session.lexiconApply.autoRedact.acknowledged = true;
      markDirty(state.session);
    })
    /**
     * Take a lexicon off the document — the only lexicon edit that subtracts.
     *
     * Three things go together and MUST go together: the applied record, the
     * detection cards (with every match and redaction decision on them), and
     * the id in `sessionAppliedLexiconIds`. Leaving the last one behind lets
     * the next burst of page text re-apply what was just removed, which reads
     * as the removal silently failing.
     *
     * The snapshot goes first, so Ctrl+Z restores all three in one step —
     * `snapshotFromSession` captures `detections` and `appliedLexicons`
     * together, which is what makes this safe enough to do without a
     * confirmation. See the action's own note.
     */
    .addCase(actions.REMOVE_LEXICON_FROM_DOCUMENT, (state, action) => {
      if (!state.session) {
        return;
      }
      const { lexiconId } = action.payload;
      const applies = (d: Detection) =>
        isLexiconDetection(d) && d.lexicon.lexiconId === lexiconId;
      const wasApplied = state.session.appliedLexicons.some(
        (r) => r.lexiconId === lexiconId
      );
      // Nothing to remove is not an edit: pushing a snapshot for it would put
      // an undo step on the stack that restores exactly what is already there.
      if (!wasApplied && !state.session.detections.some(applies)) {
        return;
      }
      pushSnapshot(state.session);

      // REDACTED MATCHES SURVIVE — see `keepRedactedMatches`. A candidate the
      // reviewer never acted on is the lexicon's suggestion and leaves with
      // it; a match they redacted is their own work, is burned into the
      // export, and stays. The card stays with it, carrying its snapshot, so
      // the surviving redaction can still say where it came from after the
      // lexicon is gone from the document (and from settings).
      const overrides = { ...current(state.session.detectionRectOverrides) };
      const kept: Detection[] = [];
      for (const detection of current(state.session.detections)) {
        if (!applies(detection)) {
          kept.push(detection);
          continue;
        }
        const survivor = keepRedactedMatches(detection);
        // Every rect of a removed card loses its override, and a SURVIVING
        // card's overrides move with their rects: dropping rect 0 renumbers
        // the rest, and `detectionRectOverrides` is keyed by that number. An
        // un-remapped override lands on a different match's box — a nudged
        // redaction jumping to another word.
        for (const key of Object.keys(overrides)) {
          if (!key.startsWith(`${detection.id}|`)) {
            continue;
          }
          const box = overrides[key];
          delete overrides[key];
          const moved = survivor?.rectKeyRemap.get(key);
          if (moved && box) {
            overrides[moved] = box;
          }
        }
        if (survivor) {
          kept.push(survivor.detection);
        }
      }
      state.session.detections = kept;
      state.session.detectionRectOverrides = overrides;
      state.session.appliedLexicons = current(
        state.session.appliedLexicons
      ).filter((r) => r.lexiconId !== lexiconId);
      state.session.lexiconApply.sessionAppliedLexiconIds =
        state.session.lexiconApply.sessionAppliedLexiconIds.filter(
          (id) => id !== lexiconId
        );
      retireLexiconRunSummary(state.session);
      markDirty(state.session);
    })
    .addCase(actions.LEXICON_APPLY_FAILURE, (state, action) => {
      // Reported, even if badly. Same reasoning as SUCCESS above — and cleared
      // before the requestId guard, so a stale run's failure still releases a
      // notice that would otherwise have nothing left to end it.
      state.ocrReloadInProgress = false;
      const apply = state.session?.lexiconApply;
      if (!apply || apply.requestId !== action.payload.requestId) {
        return;
      }
      apply.status = 'error';
      apply.errorMessage = action.payload.message;
    })

    // --- "Send to" integrations (VE-23442) --------------------------------
    .addCase(actions.PATCH_INTEGRATION_STATE, (state, action) => {
      const { key, patch } = action.payload;
      Object.assign(state.integrations[key], patch);
    })

    // --- View / Tool -----------------------------------------------------
    .addCase(actions.SCROLL_TO_PAGE, (state, action) => {
      if (state.session) {
        state.session.currentPage = action.payload.page;
        state.session.scrollToY = action.payload.y ?? null;
        state.session.scrollAlign = action.payload.align ?? 'center';
        // Bump so the canvas re-scrolls even when the target equals the
        // current page (the user may have scrolled away since).
        state.session.scrollToken = (state.session.scrollToken ?? 0) + 1;
      }
    })
    .addCase(actions.SET_ZOOM, (state, action) => {
      if (!state.session) {
        return;
      }
      state.session.zoom = action.payload.zoom;
      if (action.payload.mode) {
        state.session.zoomMode = action.payload.mode;
      }
    })
    .addCase(actions.SET_TOOL, (state, action) => {
      if (state.session) {
        state.session.activeTool = action.payload.tool;
      }
    })
    .addCase(actions.DISMISS_REDACTION_COMPLETE_NOTICE, (state) => {
      state.redactionCompleteNotice = null;
    })
    .addCase(actions.SET_TAB, (state, action) => {
      // Answering the notice by viewing the file dispatches an ordinary
      // SET_TAB, and any other deliberate tab move makes it stale too.
      state.redactionCompleteNotice = null;
      if (state.session) {
        state.session.activeTab = action.payload.tab;
      }
    })
    .addCase(actions.ADD_WATERMARK, (state, action) => {
      if (!state.session) {
        return;
      }
      const mark = {
        ...action.payload,
        opacity: clampWatermarkOpacity(action.payload.opacity),
      };
      // While the panel is open this lands on the PREVIEW — no snapshot, no
      // dirty flag, nothing the export can see. Redirecting here rather than at
      // the dispatch sites is what keeps the canvas overlay and the
      // APPLY_LIBRARY_WATERMARK saga unchanged: they dispatch what they always
      // dispatched, and the preview catches it.
      if (state.watermarkPreview) {
        state.watermarkPreview.marks.push(mark);
        // Adding a mark is a request for watermarks, so it turns the panel's
        // switch back on — as editing does (UPDATE below) and as the Bates
        // panel does. An auto-applied mark was asked for by nobody.
        if (!action.meta?.auto) {
          state.watermarkPreview.enabled = true;
        }
        return;
      }
      pushSnapshot(state.session);
      state.session.watermarks.push(mark);
      // An AUTO-APPLIED mark does not dirty the document.
      //
      // Nobody asked for it. Dirtying on its account meant a reviewer who
      // opened a file and closed it again — touching nothing — was met with
      // "This document has unsaved changes. Leave anyways?" and had to answer
      // for an edit the app made on their behalf while they watched. Worse,
      // the honest answer ("discard") reads as losing work.
      //
      // Nothing is lost by not saving it: auto-apply keys off `lastSavedAt`
      // being null, so a document left unsaved is stamped again the next time
      // it is opened. The mark is still real for this session — it draws, it
      // undoes, and both export and a manual save persist it, because those
      // write the session snapshot whatever its status. All that is skipped is
      // the AUTOSAVE, which is the only thing here that would have written to
      // a document on nobody's instruction.
      //
      // The snapshot above is still pushed, deliberately: Undo is how an
      // operator gets rid of a mark they did not ask for, and it should work
      // the same way it would for one they added themselves.
      if (!action.meta?.auto) {
        markDirty(state.session);
      }
    })
    .addCase(actions.SET_BATES_STAMP, (state, action) => {
      if (!state.session) {
        return;
      }
      // Same guard, and the same reason, as CLEAR below: reopening the panel on
      // an already-stamped document and pressing Done without changing anything
      // would otherwise push an undo entry and dirty the session — producing an
      // autosave and an "unsaved work" prompt for a no-op.
      if (
        JSON.stringify(state.session.batesStamp) ===
        JSON.stringify(action.payload.settings)
      ) {
        return;
      }
      pushSnapshot(state.session);
      state.session.batesStamp = action.payload.settings;
      // Numbering is on again, so nothing is being kept for it any more.
      state.session.batesStampOff = null;
      markDirty(state.session);
    })
    .addCase(actions.CLEAR_BATES_STAMP, (state, action) => {
      if (!state.session) {
        return;
      }
      const { batesStamp, batesStampOff } = state.session;
      // Switching off KEEPS the settings: what the panel was showing, else
      // what was applied.
      const keep = action.payload.keep ?? batesStamp ?? batesStampOff;
      // Nothing numbered and nothing to remember — there is no decision here
      // to record.
      if (!batesStamp && !keep) {
        return;
      }
      // Already off with the same settings: a genuine no-op, and the reason
      // both this and the check above exist at all. Without them a repeat
      // would push an undo snapshot and mark the session dirty, producing an
      // autosave and a "you have unsaved work" prompt for no change.
      if (
        !batesStamp &&
        JSON.stringify(batesStampOff) === JSON.stringify(keep)
      ) {
        return;
      }
      // NOTE: a document that was never numbered DOES record the off. It used
      // to bail here on the reasoning that there is no numbering to switch off,
      // which was right while the only caller was a dialog dismissal. The
      // switch now applies itself, so this carries a decision the reviewer made
      // on purpose — and dropping it meant reopening the panel found no `kept`,
      // read the document as "never numbered", and helpfully turned numbering
      // back ON. Clicking the rail icon must never be what re-enables it.
      pushSnapshot(state.session);
      state.session.batesStamp = null;
      state.session.batesStampOff = keep;
      markDirty(state.session);
    })
    // Deliberately no `pushSnapshot` and no `markDirty`: a preview is not a
    // change to the document. It is also guarded against nothing — unlike the
    // two cases above it cannot produce an autosave, so a repeat write costs a
    // re-render and nothing else.
    .addCase(actions.SET_BATES_PREVIEW, (state, action) => {
      state.batesPreview = {
        settings: action.payload.settings,
        enabled: action.payload.enabled,
      };
    })
    .addCase(actions.CLEAR_BATES_PREVIEW, (state) => {
      state.batesPreview = null;
    })
    .addCase(actions.START_WATERMARK_PREVIEW, (state) => {
      if (!state.session) {
        return;
      }
      // A COPY of the marks, not a reference: the preview is edited in place
      // from here, and immer would otherwise have the session's own array
      // following every drag.
      state.watermarkPreview = {
        marks: state.session.watermarks.map((w) => ({ ...w })),
        enabled: !state.session.watermarksOff,
      };
    })
    .addCase(actions.SET_WATERMARKS_OFF, (state, action) => {
      if (!state.session) {
        return;
      }
      const { off } = action.payload;
      // Same no-op guard the commits use: nothing dirties the document or
      // pushes an undo entry for a flag that already reads that way.
      if (state.session.watermarksOff === off) {
        return;
      }
      pushSnapshot(state.session);
      state.session.watermarksOff = off;
      markDirty(state.session);
      // The marks are NOT touched — see the action's note. An open preview has
      // its own switch dragged along so the page stops drawing immediately
      // and the panel does not then report the toggle as still unapplied.
      if (state.watermarkPreview) {
        state.watermarkPreview.enabled = !off;
      }
    })
    .addCase(actions.COMMIT_WATERMARK_PREVIEW, (state) => {
      const preview = state.watermarkPreview;
      state.watermarkPreview = null;
      if (!state.session || !preview) {
        return;
      }
      // Off KEEPS the marks and records the switch: it hides them from the page
      // and the export without deleting them, so turning it back on restores
      // them. (It used to commit an empty list, which made off a delete.)
      const next = preview.marks;
      const nextOff = !preview.enabled;
      // Guarded like the Bates commit: opening the panel, looking, and pressing
      // Apply must not push an undo entry or queue an autosave for a document
      // that did not change.
      if (
        JSON.stringify(state.session.watermarks) === JSON.stringify(next) &&
        state.session.watermarksOff === nextOff
      ) {
        return;
      }
      pushSnapshot(state.session);
      state.session.watermarks = next;
      state.session.watermarksOff = nextOff;
      markDirty(state.session);
    })
    .addCase(actions.CLEAR_WATERMARK_PREVIEW, (state) => {
      state.watermarkPreview = null;
    })
    .addCase(actions.REMOVE_WATERMARK, (state, action) => {
      if (!state.session) {
        return;
      }
      if (state.session.selectedWatermarkId === action.payload.instanceId) {
        // Otherwise the selection outlives the mark it points at. Applies to
        // the preview too — selection is view state and is not previewed.
        state.session.selectedWatermarkId = null;
      }
      if (state.watermarkPreview) {
        state.watermarkPreview.marks = state.watermarkPreview.marks.filter(
          (w) => w.instanceId !== action.payload.instanceId
        );
        return;
      }
      pushSnapshot(state.session);
      state.session.watermarks = state.session.watermarks.filter(
        (w) => w.instanceId !== action.payload.instanceId
      );
      markDirty(state.session);
    })
    .addCase(actions.UPDATE_WATERMARK, (state, action) => {
      if (!state.session) {
        return;
      }
      const list = state.watermarkPreview
        ? state.watermarkPreview.marks
        : state.session.watermarks;
      const wm = list.find((w) => w.instanceId === action.payload.instanceId);
      if (!wm) {
        return;
      }
      // Previewing: an on-canvas drag or a slider moves the preview only. This
      // is also what stops a drag from pushing one undo entry per mouse-up for
      // a position the operator has not accepted.
      if (!state.watermarkPreview) {
        pushSnapshot(state.session);
      } else {
        // Editing a mark turns the switch back on: Apply leaving the marks
        // hidden after the reviewer changed one read as the edit being lost.
        state.watermarkPreview.enabled = true;
      }
      Object.assign(wm, action.payload.patch);
      // Opacity is capped (AC#8) no matter which field the patch touched.
      if (action.payload.patch.opacity !== undefined) {
        wm.opacity = clampWatermarkOpacity(wm.opacity);
      }
      if (!state.watermarkPreview) {
        markDirty(state.session);
      }
    })
    .addCase(actions.HYDRATE_WATERMARK_IMAGES, (state, action) => {
      if (!state.session) {
        return;
      }
      // No pushSnapshot, no markDirty: this restores what the asset already
      // described, it does not change the document.
      //
      // WHERE this lands depends on why it arrived — see the action's `origin`.
      // A restored raster is the document's own artwork and goes to the
      // session (mirrored into the preview so the panel shows it too); a
      // derived one belongs to an edit the operator has not accepted, so while
      // a preview is open it goes there and nowhere else.
      const lists =
        action.payload.origin === 'restore'
          ? [
              state.session.watermarks,
              ...(state.watermarkPreview ? [state.watermarkPreview.marks] : []),
            ]
          : [state.watermarkPreview?.marks ?? state.session.watermarks];
      for (const { instanceId, imageDataUrl, imageBaseDataUrl } of action
        .payload.images) {
        for (const list of lists) {
          const wm = list.find((w) => w.instanceId === instanceId);
          if (wm) {
            wm.imageDataUrl = imageDataUrl;
            if (imageBaseDataUrl) {
              // Held for the rest of the session: every later black-&-white or
              // tint change re-derives from it, and it is what an undone
              // removal re-uploads from, so the asset can be deleted eagerly.
              wm.imageBaseDataUrl = imageBaseDataUrl;
            }
          }
        }
      }
      // The other half of the same answer (VE-28770): which marks this open
      // could NOT read. Recorded on the session rather than on the mark so it
      // cannot reach the asset — it is a fact about this open, not about the
      // document. Restore only: an `'edit'` redraw has no failure category.
      if (action.payload.origin === 'restore') {
        state.session.watermarkImageRestoreFailures =
          action.payload.failedInstanceIds ?? [];
      }
    })
    .addCase(actions.SET_WATERMARK_IMAGE_ASSET, (state, action) => {
      if (!state.session) {
        return;
      }
      // Follows the mark into the PREVIEW, like the three mutating actions
      // above. Since 1.3.0 the body stores a reference rather than the raster,
      // so a mark that reaches the document without this id references no
      // artwork at all — and while the panel is open the mark this upload
      // belongs to lives in the preview, where the session lookup would miss
      // it and drop the id on the floor.
      const previewMark = state.watermarkPreview?.marks.find(
        (w) => w.instanceId === action.payload.instanceId
      );
      if (previewMark) {
        previewMark.imageAssetId = action.payload.imageAssetId;
        return;
      }
      const wm = state.session.watermarks.find(
        (w) => w.instanceId === action.payload.instanceId
      );
      if (!wm) {
        // The mark was removed while its raster was uploading. Nothing to
        // record; the orphaned asset is collected by the save-time reconcile.
        return;
      }
      wm.imageAssetId = action.payload.imageAssetId;
      // Dirty, not undoable — see the action's doc comment. Without this the
      // id can sit unsaved on a document the user considers finished, and the
      // persisted mark then references no artwork at all.
      //
      // Except for an AUTO-APPLIED mark on a document that has never been
      // saved and has nothing else pending (VE-30011). ADD_WATERMARK left that
      // document clean on purpose; dirtying it here, a second later when the
      // upload lands, brought back the "unsaved changes" prompt for a file
      // nobody touched. No persisted body exists yet to be missing the id, and
      // whatever writes one later — an edit's autosave, a manual save, an
      // export — writes the session snapshot, id included. Once ANY save has
      // happened, or one is in flight, a body may already lack the id, so the
      // ordinary rule applies.
      const autoOnUntouched =
        action.meta?.auto === true &&
        state.session.save.status === 'clean' &&
        state.session.save.lastSavedAt === null;
      if (!autoOnUntouched) {
        markDirty(state.session);
      }
    })
    .addCase(actions.SELECT_WATERMARK, (state, action) => {
      if (state.session) {
        // View-only: not undoable, doesn't mark the doc dirty.
        state.session.selectedWatermarkId = action.payload.instanceId;
      }
    })
    .addCase(actions.SET_WATERMARK_EDITING, (state, action) => {
      if (state.session) {
        // View-only flag: not undoable, doesn't mark the doc dirty.
        state.session.watermarkEditing = action.payload.editing;
        if (!action.payload.editing) {
          // Leaving edit mode drops the selection with it: handles disappear,
          // so a selection nothing can show would just be stale state.
          state.session.selectedWatermarkId = null;
        }
      }
    })
    .addCase(actions.SET_DETECTIONS_TAB, (state, action) => {
      if (state.session) {
        state.session.detectionsTab = action.payload.tab;
      }
    })
    .addCase(actions.SET_REDACTIONS_SUBTAB, (state, action) => {
      if (state.session) {
        state.session.redactionsSubtab = action.payload.subtab;
      }
    })

    // --- Detections ------------------------------------------------------
    .addCase(actions.TOGGLE_DETECTION, (state, action) => {
      if (!state.session) {
        return;
      }
      pushSnapshot(state.session);
      const det = state.session.detections.find(
        (d) => d.id === action.payload.detectionId
      );
      if (!det) {
        return;
      }
      if (hasMatchIds(det)) {
        // Per-match detections: whole-card toggle flips the live (non-deleted)
        // match set.
        const all = liveMatchIdsOf(det);
        const cur = new Set(det.redactedMatchIds ?? []);
        const allRedacted = all.length > 0 && all.every((id) => cur.has(id));
        const next = allRedacted ? [] : all;
        const res = deriveDetectionRedaction(det, next);
        det.redactedMatchIds = res.redactedMatchIds;
        det.checkedPages = res.checkedPages;
        det.checked = res.checked;
      } else {
        const updated = toggleDetection(det);
        Object.assign(det, updated);
      }
      // Any path that redacts a detection also promotes it into the
      // Redactions list. Unredacting (result falsy) leaves it there — only
      // Delete / reset clears `addedToRedactions`.
      if (det.checked === true || det.checked === 'partial') {
        det.addedToRedactions = true;
      }
      markDirty(state.session);
    })
    /**
     * The multi-select bar's bulk redact / unredact: ONE snapshot for the
     * whole batch, so ctrl-Z reverses the click the user made rather than
     * the rows it happened to touch (VE-28716).
     *
     * Per detection this mirrors TOGGLE_DETECTION's two shapes
     * (`setDetectionRedacted` and the match-id branch here), except that the
     * target state is given rather than inferred, so a selection holding both
     * redacted and unredacted rows ends up uniform. That also makes it diverge
     * on a `'partial'` detection: `redacted: false` clears it, where the toggle
     * would redact the rest — which is what bulk Unredact relies on (VE-30158).
     * Ids that no longer exist are skipped, not an error: the selection can
     * outlive a row.
     */
    .addCase(actions.SET_DETECTIONS_REDACTED, (state, action) => {
      if (!state.session) {
        return;
      }
      const { detectionIds, redacted } = action.payload;
      if (detectionIds.length === 0) {
        return;
      }
      const wanted = new Set(detectionIds);
      const targets = state.session.detections.filter((d) => wanted.has(d.id));
      if (targets.length === 0) {
        return;
      }
      // After the existence check, so a selection of stale ids leaves no
      // empty step in the history.
      pushSnapshot(state.session);
      for (const det of targets) {
        if (hasMatchIds(det)) {
          const res = deriveDetectionRedaction(
            det,
            redacted ? liveMatchIdsOf(det) : []
          );
          det.redactedMatchIds = res.redactedMatchIds;
          det.checkedPages = res.checkedPages;
          det.checked = res.checked;
        } else {
          Object.assign(det, setDetectionRedacted(det, redacted));
        }
        // Same promotion rule as TOGGLE_DETECTION: anything redacted joins the
        // Redactions list, and unredacting leaves it there — only Delete /
        // reset clears `addedToRedactions`.
        if (det.checked === true || det.checked === 'partial') {
          det.addedToRedactions = true;
        }
      }
      markDirty(state.session);
    })
    .addCase(actions.TOGGLE_DETECTION_PAGE, (state, action) => {
      if (!state.session) {
        return;
      }
      const det = state.session.detections.find(
        (d) => d.id === action.payload.detectionId
      );
      if (!det) {
        return;
      }
      pushSnapshot(state.session);
      if (hasMatchIds(det)) {
        // Per-match detections: flip every live match on the toggled page.
        const occ = det.occurrences.find((o) => o.page === action.payload.page);
        const deleted = new Set(det.deletedMatchIds ?? []);
        const pageIds = (occ?.matchIds ?? (occ?.id ? [occ.id] : [])).filter(
          (id) => !deleted.has(id)
        );
        const cur = new Set(det.redactedMatchIds ?? []);
        const pageRedacted =
          pageIds.length > 0 && pageIds.every((id) => cur.has(id));
        for (const id of pageIds) {
          if (pageRedacted) {
            cur.delete(id);
          } else {
            cur.add(id);
          }
        }
        const res = deriveDetectionRedaction(det, cur);
        det.redactedMatchIds = res.redactedMatchIds;
        det.checkedPages = res.checkedPages;
        det.checked = res.checked;
      } else {
        const checkedPages = det.checkedPages ?? [];
        const pageIdx = checkedPages.indexOf(action.payload.page);
        const nextCheckedPages =
          pageIdx === -1
            ? [...checkedPages, action.payload.page]
            : checkedPages.filter((p) => p !== action.payload.page);
        det.checkedPages = nextCheckedPages;
        det.checked = deriveGroupCheckedState(
          det.occurrences,
          nextCheckedPages
        );
      }
      if (det.checked === true || det.checked === 'partial') {
        det.addedToRedactions = true;
      }
      markDirty(state.session);
    })
    .addCase(actions.TOGGLE_DETECTION_MATCH, (state, action) => {
      if (!state.session) {
        return;
      }
      const det = state.session.detections.find(
        (d) => d.id === action.payload.detectionId
      );
      if (!det || !hasMatchIds(det)) {
        return;
      }
      pushSnapshot(state.session);
      const cur = new Set(det.redactedMatchIds ?? []);
      if (cur.has(action.payload.matchId)) {
        cur.delete(action.payload.matchId);
      } else {
        cur.add(action.payload.matchId);
      }
      const res = deriveDetectionRedaction(det, cur);
      det.redactedMatchIds = res.redactedMatchIds;
      det.checkedPages = res.checkedPages;
      det.checked = res.checked;
      if (det.checked !== false) {
        det.addedToRedactions = true;
      }
      markDirty(state.session);
    })
    .addCase(actions.DELETE_DETECTION_MATCH, (state, action) => {
      if (!state.session) {
        return;
      }
      const det = state.session.detections.find(
        (d) => d.id === action.payload.detectionId
      );
      if (!det || !hasMatchIds(det)) {
        return;
      }
      const { matchId } = action.payload;
      const alreadyDeleted = (det.deletedMatchIds ?? []).includes(matchId);
      if (alreadyDeleted) {
        return;
      }
      pushSnapshot(state.session);
      det.deletedMatchIds = [...(det.deletedMatchIds ?? []), matchId];
      // A deleted match can't stay redacted.
      const cur = (det.redactedMatchIds ?? []).filter((id) => id !== matchId);
      if (liveMatchIdsOf(det).length === 0) {
        // Last live match deleted → the detection drops out of the Redactions
        // list entirely (back to a candidate, mirroring whole-detection
        // delete); nothing left to remember as deleted.
        det.addedToRedactions = false;
        det.deletedMatchIds = undefined;
        det.redactedMatchIds = [];
        det.checkedPages = [];
        det.checked = false;
        dropOrphanedLexiconDetections(state.session);
      } else {
        const res = deriveDetectionRedaction(det, cur);
        det.redactedMatchIds = res.redactedMatchIds;
        det.checkedPages = res.checkedPages;
        det.checked = res.checked;
      }
      markDirty(state.session);
    })
    .addCase(actions.REMOVE_DETECTIONS_FROM_REDACTIONS, (state, action) => {
      if (!state.session) {
        return;
      }
      const targets = new Set(action.payload.detectionIds);
      const willChange = state.session.detections.some(
        (d) =>
          targets.has(d.id) &&
          (d.addedToRedactions ||
            d.checked !== false ||
            !!d.checkedPages?.length)
      );
      if (!willChange) {
        return;
      }
      pushSnapshot(state.session);
      for (const det of state.session.detections) {
        if (targets.has(det.id)) {
          det.addedToRedactions = false;
          det.checked = false;
          det.checkedPages = [];
          det.redactedMatchIds = [];
          det.deletedMatchIds = undefined;
        }
      }
      dropOrphanedLexiconDetections(state.session);
      markDirty(state.session);
    })
    .addCase(actions.TOGGLE_DETECTION_EXPANDED, (state, action) => {
      if (!state.session) {
        return;
      }
      const det = state.session.detections.find(
        (d) => d.id === action.payload.detectionId
      );
      if (det) {
        det.expanded = !det.expanded;
      }
    })
    .addCase(actions.SET_DETECTION_FILTER, (state, action) => {
      if (!state.session) {
        return;
      }
      const { category, piiType, enabled } = action.payload;
      const list = state.session.detectionFilter[category];
      if (enabled && !list.includes(piiType)) {
        list.push(piiType);
      }
      if (!enabled) {
        const idx = list.indexOf(piiType);
        if (idx !== -1) {
          list.splice(idx, 1);
        }
      }
    })
    .addCase(actions.SET_LEXICON_FILTER, (state, action) => {
      if (!state.session) {
        return;
      }
      const { lexiconId, visible } = action.payload;
      const hidden = new Set(state.session.lexiconFilterUnchecked);
      if (visible) {
        hidden.delete(lexiconId);
      } else {
        hidden.add(lexiconId);
      }
      state.session.lexiconFilterUnchecked = [...hidden];
    })
    .addCase(actions.SET_ALL_LEXICON_FILTERS, (state, action) => {
      if (!state.session) {
        return;
      }
      state.session.lexiconFilterUnchecked = action.payload.visible
        ? []
        : [...new Set(action.payload.lexiconIds)];
    })
    .addCase(actions.SET_DETECTION_SOURCE, (state, action) => {
      if (state.session) {
        state.session.detectionSource = action.payload.source;
      }
    })
    .addCase(actions.SET_ALL_DETECTION_FILTERS, (state, action) => {
      if (!state.session) {
        return;
      }
      const { enabled, direct, indirect } = action.payload;
      const apply = (list: PiiTypeId[], types: PiiTypeId[]) => {
        const next = new Set(list);
        for (const t of types) {
          if (enabled) {
            next.add(t);
          } else {
            next.delete(t);
          }
        }
        return [...next];
      };
      state.session.detectionFilter.direct = apply(
        state.session.detectionFilter.direct,
        direct
      );
      state.session.detectionFilter.indirect = apply(
        state.session.detectionFilter.indirect,
        indirect
      );
    })
    .addCase(actions.SET_DETECTION_SEARCH, (state, action) => {
      if (state.session) {
        state.session.detectionSearch = action.payload.phrase;
      }
    })
    .addCase(actions.SET_DETECTION_GEOMETRY_OVERRIDE, (state, action) => {
      if (!state.session) {
        return;
      }
      pushSnapshot(state.session);
      state.session.detectionRectOverrides[action.payload.key] =
        action.payload.geometry;
      markDirty(state.session);
    })
    // --- Manual Redactions ----------------------------------------------
    .addCase(actions.CREATE_MANUAL_REDACTION, (state, action) => {
      if (!state.session) {
        return;
      }
      pushSnapshot(state.session);
      const [stamped] = withDefaultRegionNames(state.session.redactions, [
        action.payload.redaction,
      ]);
      state.session.redactions.push(stamped!);
      // A fresh manual draw owns the selection — replace, don't extend.
      state.session.selectedRedactionIds = [stamped!.id];
      markDirty(state.session);
    })
    .addCase(actions.CREATE_MANUAL_REDACTIONS, (state, action) => {
      if (!state.session || action.payload.redactions.length === 0) {
        return;
      }
      pushSnapshot(state.session);
      const stamped = withDefaultRegionNames(
        state.session.redactions,
        action.payload.redactions
      );
      state.session.redactions.push(...stamped);
      // Bulk-created redactions don't take selection — there's no
      // single anchor to make current, and a multi-select with N hits
      // would surprise the user mid-flow.
      markDirty(state.session);
    })
    .addCase(actions.CREATE_OR_MERGE_TEXT_REDACTIONS, (state, action) => {
      if (!state.session || action.payload.redactions.length === 0) {
        return;
      }
      pushSnapshot(state.session);
      const redactions = state.session.redactions;
      // Boxes the user ends up "owning" after this action — merged-into
      // existing ids plus freshly created ids — so the caller's follow-up
      // Delete acts on the whole group.
      const resultIds: string[] = [];
      const created: Redaction[] = [];
      // If this gesture extends a span that already carries a redaction code,
      // the freshly created rows join that span and should inherit the code
      // so the whole unified selection stays consistently coded. Captured
      // from the first merged-into box that has one.
      let mergedCode: Redaction['redactionCode'];
      for (const incoming of action.payload.redactions) {
        // Only the region-text path feeds this; guard anyway so a stray
        // non-text-select box just gets created rather than merged.
        if (incoming.origin === 'text-select') {
          // Merge into an existing ACTIVE text-select box on the same page
          // that this selection overlaps. Excluded targets: keyword
          // redactions (now their own `origin: 'keyword'`), plus polygon /
          // page-redact / manual boxes. Mutating in place means a later
          // incoming box can also grow this same target.
          const candidates = redactions.filter(
            (r) =>
              r.origin === 'text-select' &&
              r.redacted !== false &&
              r.page === incoming.page
          );
          const target = candidates.find((r) =>
            rectsOverlap(r.geometry, incoming.geometry)
          );
          if (target) {
            target.geometry = unionBox(target.geometry, incoming.geometry);
            // Re-stamp the merged-into box with this gesture's selection id so
            // the extended selection re-unifies under one group (clicking any
            // row selects the whole new extent). Crucially, re-stamp EVERY box
            // that shared the merged-into box's old id too — not just the one
            // row this incoming box overlapped. Otherwise the rest of the
            // pre-existing span keeps the old id and fragments off, so the
            // rows above/below the join point no longer select with the group.
            if (incoming.textSelectionId) {
              const oldId = target.textSelectionId;
              target.textSelectionId = incoming.textSelectionId;
              if (oldId && oldId !== incoming.textSelectionId) {
                for (const sibling of redactions) {
                  if (sibling !== target && sibling.textSelectionId === oldId) {
                    sibling.textSelectionId = incoming.textSelectionId;
                    if (!resultIds.includes(sibling.id)) {
                      resultIds.push(sibling.id);
                    }
                  }
                }
              }
            }
            // Adopt the incoming selection's phrase so the merged box joins
            // the new selection's right-panel card. The panel groups text-
            // select redactions by `phrase|page`; without this the merged-
            // into box keeps its old phrase and lingers as its own card,
            // so a region drag over two existing redactions left three
            // cards instead of one.
            if (incoming.phrase) {
              target.phrase = incoming.phrase;
            }
            if (target.redactionCode && !mergedCode) {
              mergedCode = target.redactionCode;
            }
            if (!resultIds.includes(target.id)) {
              resultIds.push(target.id);
            }
            continue;
          }
        }
        created.push(incoming);
        resultIds.push(incoming.id);
      }
      if (created.length > 0) {
        let toPush = withDefaultRegionNames(redactions, created);
        const code = mergedCode;
        if (code) {
          toPush = toPush.map((c) =>
            c.redactionCode ? c : { ...c, redactionCode: code }
          );
        }
        redactions.push(...toPush);
      }
      state.session.selectedRedactionIds = resultIds;
      markDirty(state.session);
    })
    .addCase(actions.MOVE_REDACTION, (state, action) => {
      if (!state.session) {
        return;
      }
      pushSnapshot(state.session);
      const r = state.session.redactions.find(
        (x) => x.id === action.payload.id
      );
      if (r) {
        // Polygon redactions carry their own vertices; translate them by the
        // same delta as the bounding box so the shape moves with the box.
        // Guarded so only custom polygons ever have `points` mutated.
        if (r.isCustomPolygon && r.points) {
          const dx = action.payload.geometry.x - r.geometry.x;
          const dy = action.payload.geometry.y - r.geometry.y;
          r.points = r.points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
        }
        r.geometry = action.payload.geometry;
      }
      markDirty(state.session);
    })
    .addCase(actions.RESIZE_REDACTION, (state, action) => {
      if (!state.session) {
        return;
      }
      pushSnapshot(state.session);
      const r = state.session.redactions.find(
        (x) => x.id === action.payload.id
      );
      if (r) {
        r.geometry = action.payload.geometry;
      }
      markDirty(state.session);
    })
    .addCase(actions.UPDATE_REDACTION_POINTS, (state, action) => {
      if (!state.session) {
        return;
      }
      pushSnapshot(state.session);
      const r = state.session.redactions.find(
        (x) => x.id === action.payload.id
      );
      const pts = action.payload.points;
      // Only reshape polygon redactions, and ignore degenerate vertex sets
      // (zero width/height) — those would collapse the shape and leave burn-in
      // with no strips.
      if (r && r.isCustomPolygon && pts.length >= 3) {
        const xs = pts.map((p) => p.x);
        const ys = pts.map((p) => p.y);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const width = Math.max(...xs) - minX;
        const height = Math.max(...ys) - minY;
        if (width > 0 && height > 0) {
          r.points = pts.map((p) => ({ x: p.x, y: p.y }));
          // Keep the bounding box in sync so non-polygon-aware consumers
          // (thumbnails, hit-testing) track the reshaped polygon.
          r.geometry = { x: minX, y: minY, width, height };
        }
      }
      markDirty(state.session);
    })
    .addCase(actions.SET_REDACTION_NAME, (state, action) => {
      if (!state.session) {
        return;
      }
      const r = state.session.redactions.find(
        (x) => x.id === action.payload.id
      );
      if (!r) {
        return;
      }
      const next = action.payload.name.trim();
      // No-op guard before pushSnapshot so committing the same name
      // (or whitespace when the redaction has no custom name) doesn't
      // burn an undo step + dirty the document for an empty change.
      const setting = !!next;
      const clearing = !next;
      const same = setting ? r.name === next : !r.name;
      if (same) {
        return;
      }
      pushSnapshot(state.session);
      if (setting) {
        r.name = next;
      } else if (clearing) {
        // Treat empty/whitespace as "remove custom name"; the card
        // falls back to whatever default it derives at render time.
        delete r.name;
      }
      markDirty(state.session);
    })
    .addCase(actions.DELETE_REDACTION, (state, action) => {
      if (!state.session) {
        return;
      }
      pushSnapshot(state.session);
      const removed = state.session.redactions.find(
        (r) => r.id === action.payload.id
      );
      state.session.redactions = state.session.redactions.filter(
        (r) => r.id !== action.payload.id
      );
      // Splitting a text selection: if the deleted row had rows above AND
      // below it, the rows below get a fresh textSelectionId so they no longer
      // select with the rows above (the gap severs the group).
      if (removed) {
        resplitTextSelectionsAfterDelete(state.session.redactions, [removed]);
      }
      // Drop the just-deleted id from the selection set so a stale highlight
      // doesn't linger on the next-rendered redaction in its slot.
      state.session.selectedRedactionIds =
        state.session.selectedRedactionIds.filter(
          (id) => id !== action.payload.id
        );
      markDirty(state.session);
    })
    .addCase(actions.DELETE_REDACTIONS, (state, action) => {
      if (!state.session) {
        return;
      }
      const drop = new Set(action.payload.ids);
      // Guard before pushSnapshot so an empty-ids dispatch doesn't burn
      // an undo step on a no-op (would force the user to undo twice to
      // get back to their last real change).
      if (drop.size === 0) {
        return;
      }
      pushSnapshot(state.session);
      const removed = state.session.redactions.filter((r) => drop.has(r.id));
      state.session.redactions = state.session.redactions.filter(
        (r) => !drop.has(r.id)
      );
      // Re-split any text selection a deletion cut in the middle (see
      // DELETE_REDACTION). Handles multiple cuts in one batch.
      resplitTextSelectionsAfterDelete(state.session.redactions, removed);
      state.session.selectedRedactionIds =
        state.session.selectedRedactionIds.filter((id) => !drop.has(id));
      markDirty(state.session);
    })
    .addCase(actions.SET_REDACTIONS_REDACTED, (state, action) => {
      if (!state.session) {
        return;
      }
      const { ids, redacted } = action.payload;
      if (ids.length === 0) {
        return;
      }
      const targets = new Set(ids);
      // Pre-check: skip the undo snapshot if every targeted redaction is
      // already in the requested state. Otherwise toggling a phrase
      // group whose members already match the click would burn an
      // empty undo step.
      const willChange = state.session.redactions.some(
        (r) => targets.has(r.id) && (r.redacted ?? true) !== redacted
      );
      if (!willChange) {
        return;
      }
      pushSnapshot(state.session);
      for (const r of state.session.redactions) {
        if (targets.has(r.id)) {
          r.redacted = redacted;
        }
      }
      markDirty(state.session);
    })
    .addCase(actions.CONVERT_REDACTIONS_TO_KEYWORD, (state, action) => {
      if (!state.session) {
        return;
      }
      const targets = new Set(action.payload.ids);
      if (targets.size === 0) {
        return;
      }
      // Only text-select boxes are promotable — leave drawn / polygon / PII /
      // already-keyword rows untouched. Skip the undo snapshot if nothing
      // would actually change.
      const willChange = state.session.redactions.some(
        (r) => targets.has(r.id) && r.origin === 'text-select'
      );
      if (!willChange) {
        return;
      }
      pushSnapshot(state.session);
      for (const r of state.session.redactions) {
        if (targets.has(r.id) && r.origin === 'text-select') {
          r.origin = 'keyword';
          // `textSelectionId` is KEPT — keyword rows carry it too now, and
          // it's what makes a wrapped match one unit. Clearing it here split a
          // promoted span back into independent rows, so half a phrase could
          // be deleted on its own (VE-26267).
        }
      }
      markDirty(state.session);
    })
    // VE-20838: flip a redaction's "applies to every page" flag. The
    // geometry stays the same; rendering + burn-in expand to per-page
    // copies whenever the flag is true.
    .addCase(actions.TOGGLE_REDACTION_ALL_PAGES, (state, action) => {
      if (!state.session) {
        return;
      }
      pushSnapshot(state.session);
      const r = state.session.redactions.find(
        (x) => x.id === action.payload.id
      );
      if (r) {
        r.appliesToAllPages = !r.appliesToAllPages;
      }
      markDirty(state.session);
    })
    // VE-20838: bulk-remove every redaction with the same source phrase
    // (case-insensitive). Powers "Unredact All (N)" in the redaction's
    // right-click menu. Single reducer pass + one undo snapshot so it
    // shows up as one step in the undo stack.
    .addCase(actions.DELETE_REDACTIONS_BY_PHRASE, (state, action) => {
      if (!state.session) {
        return;
      }
      const target = action.payload.phrase.trim().toLowerCase();
      // Guard before pushSnapshot so an empty / whitespace-only phrase
      // doesn't burn an undo step on a no-op (the user would have to
      // Ctrl-Z twice to get back to their last real change).
      if (!target) {
        return;
      }
      const dropIds = new Set(
        state.session.redactions
          .filter((r) => (r.phrase ?? '').trim().toLowerCase() === target)
          .map((r) => r.id)
      );
      if (dropIds.size === 0) {
        return;
      }
      pushSnapshot(state.session);
      state.session.redactions = state.session.redactions.filter(
        (r) => !dropIds.has(r.id)
      );
      // Prune the just-deleted ids from selection so a follow-up Delete
      // keystroke or batch-action click doesn't dispatch against dead
      // ids (matches DELETE_REDACTION / DELETE_REDACTIONS behavior).
      state.session.selectedRedactionIds =
        state.session.selectedRedactionIds.filter((id) => !dropIds.has(id));
      markDirty(state.session);
    })
    .addCase(actions.CLEAR_REDACTIONS, (state) => {
      if (!state.session) {
        return;
      }
      pushSnapshot(state.session);
      // Clear every per-redaction object PLUS the page-level marks
      // (slip-sheet via the Remove Page tool, page drop via the
      // Delete Page tool). The toolbar "delete all" button is the only
      // live trigger and conceptually means "reset every redaction
      // decision in the document," so leaving page marks behind would
      // surprise users.
      state.session.redactions = [];
      state.session.pageRedacts = [];
      state.session.excludedPages = [];
      // PII detections are redactions too — reset their added/redacted state
      // so "delete all" actually clears them. They were previously left
      // promoted + burned-in (the sweep only touched `redactions`, not the
      // detection list), so added/redacted PII survived the wipe.
      for (const d of state.session.detections) {
        d.checked = false;
        d.checkedPages = undefined;
        d.redactedMatchIds = undefined;
        d.deletedMatchIds = undefined;
        d.addedToRedactions = false;
        d.redactionCode = undefined;
      }
      dropOrphanedLexiconDetections(state.session);
      // Drop selection too — the redactions those ids referenced no
      // longer exist, and a stale selection keeps the right-panel
      // multi-select toolbar visible with non-functional buttons
      // (the SET_REDACTIONS_REDACTED no-op guard would fire silently
      // against a now-empty redactions array).
      state.session.selectedRedactionIds = [];
      state.session.selectedDetectionRowKeys = [];
      markDirty(state.session);
    })
    .addCase(actions.SELECT_REDACTION, (state, action) => {
      if (!state.session) {
        return;
      }
      const { id, mode = 'replace' } = action.payload;
      const current = state.session.selectedRedactionIds;
      if (id === null) {
        // Null only meaningful with replace (clear); for toggle/add it's a
        // no-op since there's nothing to toggle in/out of the set.
        if (mode === 'replace') {
          state.session.selectedRedactionIds = [];
          // Replace-clear nukes the PII selection too — the canvas
          // empty-area click that fires this should reset *both* sets.
          state.session.selectedDetectionRowKeys = [];
        }
        return;
      }
      // Text-selection grouping: clicking one row box selects the whole
      // selection (every box on the same page sharing its textSelectionId).
      // Applies in every mode so toggle/add operate on the group as a unit.
      const clicked = state.session.redactions.find((r) => r.id === id);
      const groupIds = clicked?.textSelectionId
        ? state.session.redactions
            .filter(
              (r) =>
                r.textSelectionId === clicked.textSelectionId &&
                r.page === clicked.page
            )
            .map((r) => r.id)
        : [id];
      if (mode === 'replace') {
        state.session.selectedRedactionIds = groupIds;
        // Single-click on a redaction wins the "active" selection —
        // clear any prior PII selection so the user sees one focus
        // group at a time.
        state.session.selectedDetectionRowKeys = [];
        return;
      }
      if (mode === 'toggle') {
        // Toggle the group as a unit: if every member is already selected,
        // remove them all; otherwise add the whole group.
        const allSelected = groupIds.every((g) => current.includes(g));
        state.session.selectedRedactionIds = allSelected
          ? current.filter((x) => !groupIds.includes(x))
          : Array.from(new Set([...current, ...groupIds]));
        return;
      }
      // mode === 'add' (marquee / batch builders)
      state.session.selectedRedactionIds = Array.from(
        new Set([...current, ...groupIds])
      );
    })
    .addCase(actions.SELECT_REDACTIONS, (state, action) => {
      if (!state.session) {
        return;
      }
      const { ids, mode = 'replace' } = action.payload;
      if (mode === 'replace') {
        // Dedupe in case the caller passed duplicates (e.g. an overlap
        // hit-test that double-counts a redaction).
        state.session.selectedRedactionIds = Array.from(new Set(ids));
        // Replace always nukes the PII selection — marquee + click
        // both flow through here with replace by default. Add/toggle
        // (shift+marquee) leave the PII set alone so the user can
        // build cross-type batches.
        state.session.selectedDetectionRowKeys = [];
        return;
      }
      const current = new Set(state.session.selectedRedactionIds);
      if (mode === 'add') {
        for (const id of ids) {
          current.add(id);
        }
      } else {
        // mode === 'toggle': XOR each id into the set.
        for (const id of ids) {
          if (current.has(id)) {
            current.delete(id);
          } else {
            current.add(id);
          }
        }
      }
      state.session.selectedRedactionIds = Array.from(current);
    })
    .addCase(actions.SELECT_DETECTION_ROW, (state, action) => {
      if (!state.session) {
        return;
      }
      const { key, mode = 'replace' } = action.payload;
      const current = state.session.selectedDetectionRowKeys;
      if (key === null) {
        if (mode === 'replace') {
          state.session.selectedDetectionRowKeys = [];
          state.session.selectedRedactionIds = [];
        }
        return;
      }
      if (mode === 'replace') {
        state.session.selectedDetectionRowKeys = [key];
        // Single-click on PII clears any prior redaction selection
        // for the same reason the inverse path does — one active
        // selection group at a time.
        state.session.selectedRedactionIds = [];
        return;
      }
      if (mode === 'toggle') {
        state.session.selectedDetectionRowKeys = current.includes(key)
          ? current.filter((k) => k !== key)
          : [...current, key];
        return;
      }
      // mode === 'add' (marquee)
      if (!current.includes(key)) {
        state.session.selectedDetectionRowKeys = [...current, key];
      }
    })
    .addCase(actions.SELECT_DETECTION_ROWS, (state, action) => {
      if (!state.session) {
        return;
      }
      const { keys, mode = 'replace' } = action.payload;
      if (mode === 'replace') {
        state.session.selectedDetectionRowKeys = Array.from(new Set(keys));
        state.session.selectedRedactionIds = [];
        return;
      }
      const current = new Set(state.session.selectedDetectionRowKeys);
      if (mode === 'add') {
        for (const k of keys) {
          current.add(k);
        }
      } else {
        // toggle: XOR each key.
        for (const k of keys) {
          if (current.has(k)) {
            current.delete(k);
          } else {
            current.add(k);
          }
        }
      }
      state.session.selectedDetectionRowKeys = Array.from(current);
    })

    // --- Codes -----------------------------------------------------------
    .addCase(actions.ASSIGN_CODE, (state, action) => {
      if (!state.session) {
        return;
      }
      pushSnapshot(state.session);
      const { targetType, targetId, code } = action.payload;
      if (targetType === 'redaction') {
        const r = state.session.redactions.find((x) => x.id === targetId);
        if (r) {
          if (code === null) {
            delete r.redactionCode;
          } else {
            r.redactionCode = code;
          }
        }
      } else {
        const det = state.session.detections.find((d) => d.id === targetId);
        if (det) {
          if (code === null) {
            delete det.redactionCode;
          } else {
            det.redactionCode = code;
          }
        }
      }
      markDirty(state.session);
    })

    // --- Page Redact -----------------------------------------------------
    .addCase(actions.TOGGLE_PAGE_REDACT, (state, action) => {
      if (!state.session) {
        return;
      }
      pushSnapshot(state.session);
      const { page } = action.payload;
      const idx = state.session.pageRedacts.indexOf(page);
      if (idx === -1) {
        state.session.pageRedacts.push(page);
        // Redact and exclude are mutually exclusive — a page can't both ship
        // (redacted) and be dropped. Marking it redacted clears any removal
        // so the page's overlay/notice reflects a single state.
        const exIdx = state.session.excludedPages.indexOf(page);
        if (exIdx !== -1) {
          state.session.excludedPages.splice(exIdx, 1);
        }
      } else {
        state.session.pageRedacts.splice(idx, 1);
      }
      markDirty(state.session);
    })

    // --- Page Exclude (VE-20838) -----------------------------------------
    // Toggles whether a page is dropped from the burned-in output. Mutually
    // exclusive with TOGGLE_PAGE_REDACT: an excluded page won't ship, so
    // redacting it is meaningless. Excluding a page therefore clears any
    // full-page-redact state so the overlay/notice shows a single state.
    .addCase(actions.TOGGLE_PAGE_EXCLUDE, (state, action) => {
      if (!state.session) {
        return;
      }
      pushSnapshot(state.session);
      const { page } = action.payload;
      const idx = state.session.excludedPages.indexOf(page);
      if (idx === -1) {
        state.session.excludedPages.push(page);
        const redIdx = state.session.pageRedacts.indexOf(page);
        if (redIdx !== -1) {
          state.session.pageRedacts.splice(redIdx, 1);
        }
      } else {
        state.session.excludedPages.splice(idx, 1);
      }
      markDirty(state.session);
    })

    // --- Keyword Search --------------------------------------------------
    // Stamp the phrase synchronously so every controlled input bound
    // to `keywordSearch.phrase` (the rail's input, the panel's
    // KeywordSearchInput) echoes the keystroke immediately. Without
    // this, the phrase only updated when the saga's debounced
    // KEYWORD_MATCHES_READY fired, leaving the panel input visibly
    // lagging by ~150ms + match-compute time on large documents.
    // Matches still come from KEYWORD_MATCHES_READY — only the phrase
    // is hoisted ahead.
    .addCase(actions.KEYWORD_SEARCH, (state, action) => {
      if (!state.session) {
        return;
      }
      state.session.keywordSearch = {
        ...state.session.keywordSearch,
        phrase: action.payload.phrase,
      };
      // Deselect any selected redaction/PII box when a search is active (only
      // when DESELECT_ON_SEARCH is enabled — off by default; see the flag).
      if (DESELECT_ON_SEARCH && action.payload.phrase.trim()) {
        state.session.selectedRedactionIds = [];
        state.session.selectedDetectionRowKeys = [];
        state.session.selectedDetectionMaskKey = null;
      }
    })
    .addCase(actions.KEYWORD_MATCHES_READY, (state, action) => {
      if (!state.session) {
        return;
      }
      state.session.keywordSearch = {
        ...state.session.keywordSearch,
        phrase: action.payload.phrase,
        matches: action.payload.matches,
        lastSearchedAt: Date.now(),
      };
    })
    .addCase(actions.SET_KEYWORD_SEARCH_OPTIONS, (state, action) => {
      if (!state.session) {
        return;
      }
      const { matchCase, wholeWord } = action.payload;
      state.session.keywordSearch = {
        ...state.session.keywordSearch,
        // Defensive `??` so omitted fields keep their prior values.
        matchCase: matchCase ?? state.session.keywordSearch.matchCase,
        wholeWord: wholeWord ?? state.session.keywordSearch.wholeWord,
        // Clear stale matches — the saga will re-emit them via
        // KEYWORD_MATCHES_READY once it re-runs against the new options.
        matches: [],
      };
    })

    // --- Undo / Redo -----------------------------------------------------
    .addCase(actions.UNDO, (state) => {
      if (!state.session) {
        return;
      }
      // Not while a panel is previewing. UNDO restores the whole session at
      // once, so there is no per-field branch that could route it into a
      // preview — and an undo the operator cannot see is worse than one they
      // cannot make: the canvas and the panel would both go on drawing the
      // preview, and Apply would write the undone state straight back onto the
      // document. The preview has its own undo, and it is called Cancel.
      if (state.watermarkPreview || state.batesPreview) {
        return;
      }
      const snapshot = snapshotFromSession(current(state.session));
      const assets = watermarkImageAssetIds(current(state.session));
      const { stack, popped } = popToUndo(state.session.undoStack, snapshot);
      if (!popped) {
        return;
      }
      // `applyUndoSnapshot` is a superset of the plain `Object.assign` this
      // replaced: it restores the same session fields and then the two lexicon
      // extras that do not live directly on the session.
      applyUndoSnapshot(state.session, popped);
      restoreWatermarkImageAssetIds(state.session, assets);
      state.session.undoStack = stack;
      markDirty(state.session);
    })
    .addCase(actions.REDO, (state) => {
      if (!state.session) {
        return;
      }
      // Same reason as UNDO above.
      if (state.watermarkPreview || state.batesPreview) {
        return;
      }
      const snapshot = snapshotFromSession(current(state.session));
      const assets = watermarkImageAssetIds(current(state.session));
      const { stack, popped } = popToRedo(state.session.undoStack, snapshot);
      if (!popped) {
        return;
      }
      // `applyUndoSnapshot` is a superset of the plain `Object.assign` this
      // replaced: it restores the same session fields and then the two lexicon
      // extras that do not live directly on the session.
      applyUndoSnapshot(state.session, popped);
      restoreWatermarkImageAssetIds(state.session, assets);
      state.session.undoStack = stack;
      markDirty(state.session);
    })

    // --- Save / Lock -----------------------------------------------------
    // VE-28061: keyed by TDO. The export's pre-burn-in save runs on after the
    // editor closes, so these can arrive while a DIFFERENT document is open.
    // Applying them to whatever was on screen marked THAT document clean —
    // losing its unsaved edits, since both Save buttons disable on
    // `!hasUnsavedWork` and the auto-save interval skips clean sessions — and
    // gave it the exporting document's burn-in version label, which is what
    // the staleness verdict compares.
    //
    // VE-24947: matching by TDO is the point, so these resolve through
    // `sessionFor` rather than testing the active session alone. Save All
    // persists SUSPENDED documents, and their outcomes have to land on them —
    // a match is still a match when the document is parked. What the original
    // guard was protecting against is unchanged: no outcome ever reaches a
    // session whose tdoId it does not name.
    .addCase(actions.SAVE_REQUEST, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.save.status = 'saving';
      }
    })
    .addCase(actions.SAVE_SUCCESS, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (!target) {
        return;
      }
      // Stay dirty if an edit landed after the snapshot this save persisted.
      // Holds for a suspended document too: Save All snapshots it as it was
      // parked, and nothing can edit it while it is off screen — so `rev` is
      // unchanged and it goes cleanly clean.
      const editedMidSave = target.save.rev > action.payload.savedRev;
      target.save = {
        ...target.save,
        status: editedMidSave ? 'dirty' : 'clean',
        lastSavedAt: action.payload.savedAt,
        lastError: undefined,
        // VE-26422: a save with no version was not sent for burn-in, which is
        // recorded as null rather than left undefined — undefined means "no
        // label at all", which only assets predating this scheme carry.
        lastSavedBurnInVersion: action.payload.expectedRedactedVersion ?? null,
      };
    })
    .addCase(actions.SAVE_FAILURE, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (!target) {
        return;
      }
      target.save = {
        ...target.save,
        status: 'error',
        lastError: { ...action.payload, failedAt: new Date().toISOString() },
      };
    })

    // --- Redaction status (VE-21914) -------------------------------------
    // Optimistic apply: REQUEST flips the value immediately so the dropdown
    // reflects the user's choice without waiting for the GraphQL round-trip.
    // FAILURE rolls back to the prior value the saga captured.
    .addCase(actions.SET_REDACTION_STATUS_REQUEST, (state, action) => {
      if (state.session) {
        state.session.redactionStatus = action.payload.status;
        // A change always lands on a live status, so the frozen name is just
        // its label; orphan recovery only matters for the loaded-from-tag value.
        state.session.redactionStatusName = action.payload.statusLabel;
      }
    })
    .addCase(actions.SET_REDACTION_STATUS_FAILURE, (state, action) => {
      if (state.session) {
        state.session.redactionStatus = action.payload.previousStatus;
        state.session.redactionStatusName = action.payload.previousStatusName;
      }
    })

    // --- Rename (VE-21914 follow-on) -------------------------------------
    // Optimistic apply mirrors the status pattern: the inline edit popover
    // closes on confirm and the new name shows immediately. FAILURE rolls
    // back to the snapshot the UI captured at submit time.
    .addCase(actions.RENAME_DOCUMENT_REQUEST, (state, action) => {
      if (state.session) {
        state.session.documentName = action.payload.name;
      }
    })
    .addCase(actions.RENAME_DOCUMENT_SUCCESS, (state, action) => {
      // The server may have sanitized the submitted name (leading-space
      // trim, future extension fixups, etc.). Re-applying the server value
      // here keeps the UI consistent with what reload will show.
      if (state.session) {
        state.session.documentName = action.payload.name;
      }
    })
    .addCase(actions.RENAME_DOCUMENT_FAILURE, (state, action) => {
      if (state.session) {
        state.session.documentName = action.payload.previousName;
      }
    })

    // --- Canvas / text extraction ----------------------------------------
    .addCase(actions.SET_PAGE_TEXT, (state, action) => {
      if (state.session) {
        state.session.pageTexts[action.payload.page] = action.payload.text;
      }
    })
    .addCase(actions.SET_PAGE_TEXT_ITEMS, (state, action) => {
      if (state.session) {
        state.session.pageTextItems[action.payload.page] = action.payload.items;
      }
    })
    .addCase(actions.SET_PAGE_SEARCHABILITY, (state, action) => {
      if (state.session) {
        state.session.pageSearchability[action.payload.page] =
          action.payload.state;
      }
    })
    // Bulk pre-pass result (VE-25293): apply text / items / searchability /
    // dimensions for every page in one pass. Written before (and independent
    // of) rendering so the whole document's metadata is available while the
    // canvas only mounts pages near the viewport.
    .addCase(actions.SET_PAGE_TEXT_BATCH, (state, action) => {
      if (!state.session) {
        return;
      }
      for (const p of action.payload.pages) {
        state.session.pageTexts[p.page] = p.text;
        state.session.pageTextItems[p.page] = p.items;
        state.session.pageSearchability[p.page] = p.searchability;
        state.session.pageDimensions[p.page] = p.dims;
      }
    })
    // Fast dims-only pre-pass (VE-27261): streamed ahead of the full text batch
    // above so the virtualized viewers get accurate reserved heights early.
    // Writes ONLY pageDimensions — never text/searchability — so it can't
    // clobber what SET_PAGE_TEXT_BATCH owns.
    .addCase(actions.SET_PAGE_DIMENSIONS_BATCH, (state, action) => {
      if (!state.session) {
        return;
      }
      for (const p of action.payload.pages) {
        state.session.pageDimensions[p.page] = p.dims;
      }
    })
    .addCase(actions.SET_OCR_WARNING_DISMISSED, (state, action) => {
      if (state.session) {
        state.session.ocrWarningDismissed = action.payload;
      }
    })
    .addCase(actions.SET_OCR_PROCESSED_DISMISSED, (state, action) => {
      if (state.session) {
        state.session.ocrProcessedDismissed = action.payload;
      }
    })
    // Pre-pass finished or gave up. Either way the rail can drop its spinner;
    // only on success does keyword search now cover every page. A failure,
    // including one the extractor swallowed, sets `textIndexFailed`.
    .addCase(actions.PDF_TEXT_PREPASS_DONE, (state, action) => {
      if (state.session) {
        state.session.textIndexReady = true;
        state.session.textIndexFailed = action.payload?.failed === true;
      }
    })
    // Streaming pre-pass progress — updated per chunk to drive the rail's
    // "Indexing document — X of Y pages" indicator.
    .addCase(actions.SET_TEXT_INDEX_PROGRESS, (state, action) => {
      if (state.session) {
        state.session.textIndexProgress = action.payload;
      }
    })
    // The scanned-vs-blank probe has finished. Distinct from the text pre-pass
    // above: until this lands, a text-less page is only provisionally `blank`.
    .addCase(actions.PAGE_SEARCHABILITY_SCAN_DONE, (state) => {
      if (state.session) {
        state.session.searchabilityScanReady = true;
      }
    })
    .addCase(actions.SET_SEARCHABILITY_SCAN_PROGRESS, (state, action) => {
      if (state.session) {
        state.session.searchabilityScanProgress = action.payload;
      }
    })

    // --- Burn-in / export lifecycle (VE-20837) ---------------------------
    // `phase` drives the "Redact File" button spinner and progress UI.
    // Saga dispatches transition: EXPORT_REQUEST -> EXPORT_SUBMITTED ->
    // EXPORT_COMPLETED (success) or EXPORT_FAILED (error).
    //
    // VE-28061: the export outlives the editor session. SUBMITTED / COMPLETED /
    // FAILED therefore name their TDO and apply only to a session showing it —
    // a no-op when the editor is closed, and never a write into whatever other
    // document the user has opened since.
    .addCase(actions.EXPORT_REQUEST, (state) => {
      if (state.session) {
        state.session.export = { phase: 'preparing' };
        // Recorded here and not in the component, so every exit can see it —
        // see `DocumentsStore.burnInsStartedHere`. The name is captured now
        // because the session may be gone by the time anyone asks.
        state.burnInsStartedHere[state.session.tdoId] =
          state.session.documentName;
      }
    })
    .addCase(actions.EXPORT_SUBMITTED, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        // The saga uses EXPORT_SUBMITTED as the signal that burn-in proper
        // has started (after the source PDF is in memory). Switching the
        // phase here so the UI can show a more accurate "Redacting..." label.
        target.export = { phase: 'burning' };
      }
    })
    .addCase(actions.EXPORT_COMPLETED, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (!target) {
        return;
      }
      target.export = { phase: 'idle' };
      // Where the reviewer ends up. Decided here rather than in the saga so
      // the tab, the notice and the new versionHistory land in one commit —
      // a separate SET_TAB could otherwise arrive a tick after a switch and
      // flip the wrong document's tab.
      //
      // Only for the document on screen: a burn-in finishing on a suspended
      // one has nothing to interrupt and no tab to flip. Identity by reference
      // off `sessionFor`'s answer rather than a second tdoId comparison, so
      // there is one place that decides which session this is.
      if (target === state.session) {
        // `isSessionWorthKeeping`, not `hasUnsavedWork`. The question is
        // whether the burned-in file predates edits the reviewer has made, and
        // a save still in flight means exactly that — `hasUnsavedWork` excludes
        // `saving`, so a burn-in finishing during an autosave tick flipped the
        // reviewer to output that was already behind, with no notice saying so.
        if (isSessionWorthKeeping(target.save)) {
          state.redactionCompleteNotice = action.payload.tdoId;
        } else {
          target.activeTab = 'redacted-file';
        }
      }
      // REPLACE versionHistory with just the new entry, don't append.
      // The exportPush saga's rotation step deletes all prior
      // `redacted-document` assets after every burn-in (per ticket), so
      // any pre-existing entries here are stale — their backing assets
      // are gone, and rendering them would point at dead signedUris.
      // Once the retention policy changes ("keep N most recent"), this
      // becomes an append-and-trim instead.
      target.versionHistory = [
        {
          versionId: action.payload.versionId,
          version: action.payload.version,
          redactedAt: new Date().toISOString(),
          // Keep null through — coercing to '' makes downstream <Document
          // file=""> and download links navigate to current page. Consumers
          // guard explicitly for null.
          redactedAssetUrl: action.payload.redactedAssetUrl,
          name: action.payload.name,
          assetSize: action.payload.assetSize,
          numPages: action.payload.numPages,
          pagesRemoved: action.payload.pagesRemoved,
          pagesFullyRedacted: action.payload.pagesFullyRedacted,
          // VE-20840: signedUri of the redact-audit-log CSV asset (empty when
          // creation failed — the download button stays hidden in that case).
          auditLogUrl: action.payload.auditLogUrl ?? '',
          redactionCount: action.payload.redactionCount,
          textSelect: action.payload.textSelect,
          keywords: action.payload.keywords,
          pii: action.payload.pii,
          downloads: 0,
          pushedTo: [],
        },
      ];
    })
    .addCase(actions.EXPORT_FAILED, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.export = {
          phase: 'error',
          lastError: action.payload.message,
        };
      }
    })

    // VE-21915 PII re-run: flip optimistically to `processing` on request so
    // the PII subtab swaps the button for a spinner immediately; revert to
    // `not-run` if the job submission fails so the user can retry.
    .addCase(actions.RUN_PII_DETECTION, (state) => {
      if (state.session) {
        state.session.piiEngineState = 'processing';
      }
    })
    .addCase(actions.RUN_PII_DETECTION_FAILURE, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.piiEngineState = 'not-run';
      }
    })

    // EVERY ENGINE OUTCOME FROM HERE DOWN RESOLVES ITS OWN SESSION.
    //
    // These arrive whenever the API answers, and the workspace keeps several
    // documents open — so the session on screen is not necessarily the one the
    // reading is about. Unscoped, a run on A rewrote B's engine state: a
    // fabricated failure, a spinner for a task B does not have, or — worst —
    // A's detections folded into the page B's reviewer is looking at.
    //
    // `sessionFor`, not `state.session`, so a document parked in
    // `suspendedSessions` still gets its own outcome; the reviewer comes back
    // to the answer rather than to the spinner they left. Absent means the
    // document is genuinely gone, which stays a no-op.

    // On-open check settled with nothing running and no results (or errored) —
    // leave 'checking' for the "Run PII Detection" prompt (VE-27250).
    .addCase(actions.PII_NOT_RUN, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.piiEngineState = 'not-run';
      }
    })
    // On-open lifecycle check found a PII task already in flight.
    .addCase(actions.PII_DETECTION_PROCESSING, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.piiEngineState = 'processing';
      }
    })
    // Results parsed from the engine's vtn-standard asset — fold them into the
    // detection list and mark the engine done.
    .addCase(actions.PII_RESULTS_LOADED, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        // Re-apply persisted PII selections to the freshly loaded engine
        // detections — they arrive here after open, so the per-match records
        // stashed at OPEN_DOCUMENT_SUCCESS are matched now.
        //
        // NOT an assignment: this used to replace the whole list, which wiped
        // every lexicon row (VE-24948). See `mergePiiResultsIntoDetections`.
        target.detections = mergePiiResultsIntoDetections(
          target.detections,
          action.payload.detections,
          target.persistedPiiMatches
        );
        target.piiEngineState = 'done';
      }
    })

    // --- Redact Document OCR ----------------------------------------------
    .addCase(actions.OCR_PROCESSING, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.ocrEngineState = 'processing';
      }
    })
    // Optimistic, like RUN_PII_DETECTION above: the notice swaps its Run
    // button for the spinning "OCR is processing" variant on the
    // CLICK, not on the job coming back. Creating the job is a round trip, and
    // a button that sits inert for it reads as a click that missed. Reverted on
    // failure so the offer comes back and the run can be retried.
    .addCase(actions.RUN_OCR_DETECTION, (state) => {
      if (state.session) {
        state.session.ocrEngineState = 'processing';
      }
    })
    .addCase(actions.RUN_OCR_DETECTION_FAILURE, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.ocrEngineState = 'not-run';
      }
    })
    .addCase(actions.OCR_INDETERMINATE, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.ocrEngineState = 'indeterminate';
      }
    })
    .addCase(actions.OCR_REFRESH_AVAILABLE, (state, action) => {
      // Only ever an offer. The session keeps rendering the PDF it pinned until
      // the user takes the reload — see RELOAD_FOR_OCR.
      //
      // Reachable from the three states that are not an answer: `checking` and
      // `indeterminate` mean nothing has been established, and a task that
      // appears and finishes between two polls is never seen running. States
      // that ARE an answer (`not-run`, `failed`, `done`) are left alone.
      const target = sessionFor(state, action.payload.tdoId);
      const from = target?.ocrEngineState;
      if (
        target &&
        (from === 'processing' ||
          from === 'checking' ||
          from === 'indeterminate')
      ) {
        target.ocrEngineState = 'refresh-available';
      }
    })
    .addCase(actions.OCR_TIMED_OUT, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.ocrEngineState = 'timed-out';
      }
    })
    .addCase(actions.PII_DETECTION_FAILED, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.piiEngineState = 'failed';
      }
    })
    .addCase(actions.PII_TIMED_OUT, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.piiEngineState = 'timed-out';
      }
    })
    .addCase(actions.OCR_NOT_RUN, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.ocrEngineState = 'not-run';
        target.ocrManifest = null;
      }
    })
    .addCase(actions.OCR_FAILED, (state, action) => {
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.ocrEngineState = 'failed';
        // A failed run can still have published a manifest for the pages it did
        // read, but this action is reached from task status alone — so keep
        // whatever a prior load put here rather than asserting emptiness.
      }
    })
    // Not scoped: a toggle is a click on the document already in front of the
    // reviewer, dispatched and reduced in the same turn. Nothing can move the
    // session in between.
    .addCase(actions.TOGGLE_OCR_REGIONS, (state, action) => {
      if (state.session) {
        state.session.showOcrRegions = action.payload.show;
      }
    })
    .addCase(actions.OCR_MANIFEST_LOADED, (state, action) => {
      // Guarded since it was written: a manifest for another document would
      // suppress this one's "no searchable text" warning. Every engine outcome
      // above now carries the same guard, and reaches parked sessions too.
      const target = sessionFor(state, action.payload.tdoId);
      if (target) {
        target.ocrManifest = action.payload.manifest;
        target.ocrEngineState = 'done';
      }
    })
    // VE-20837 delete: drop the asset's entry from versionHistory so the
    // Redacted Files tab updates without waiting for a refetch. Mirrors
    // the optimistic add on EXPORT_COMPLETED.
    .addCase(actions.DELETE_REDACTED_DOCUMENT_SUCCESS, (state, action) => {
      if (!state.session) {
        return;
      }
      state.session.versionHistory = state.session.versionHistory.filter(
        (v) => v.versionId !== action.payload.versionId
      );
    })

    // Cache a redacted version's REAL deleted pages, read back from the
    // redaction-data asset the burn-in consumed (see the field's doc on
    // SessionState). Keyed by versionId so switching versions doesn't reuse the
    // wrong list. The failure case writes nothing — an absent entry means "not
    // known", and consumers fall back to session.excludedPages.
    .addCase(actions.LOAD_REDACTED_VERSION_PAGES_SUCCESS, (state, action) => {
      if (!state.session) {
        return;
      }
      state.session.redactedExcludedPagesByVersion[action.payload.versionId] =
        action.payload.excludedPages;
    })

    // Optimistic bump of the per-asset download counter. The persistence
    // side-effect (writing the new count back to the asset via updateAsset)
    // is handled by the saga in exportPush.ts. Mirrors how the video flow
    // splits INCREMENT_DOWNLOAD_COUNT between reducer + saga.
    .addCase(actions.INCREMENT_REDACTED_DOCUMENT_DOWNLOAD, (state, action) => {
      if (!state.session) {
        return;
      }
      const entry = state.session.versionHistory.find(
        (v) => v.versionId === action.payload.versionId
      );
      if (entry) {
        entry.downloads = (entry.downloads ?? 0) + 1;
      }
    })

    // --- Document comments (VE-21982) ------------------------------------
    // Mirrors common-modules/mediaDetails/reducers/mediaComments but lives
    // here so the document slice owns its own comments lifecycle. Undo/redo
    // is intentionally NOT wired through documentComments — the existing
    // editor undo stack only captures session geometry/detection state, not
    // comments, and a comment edit should be auditable rather than
    // undoable.
    .addCase(actions.FETCH_DOCUMENT_COMMENTS, (state) => {
      state.isFetchingDocumentComments = true;
    })
    .addCase(actions.FETCH_DOCUMENT_COMMENTS_SUCCESS, (state, action) => {
      const {
        structuredDataObjects: { offset, records },
      } = action.payload;
      // On the first page (offset === 0) we replace the cache; subsequent
      // pages merge in. Matches the media-comments reducer, but with one
      // exception: pending (un-persisted) comments — `done === false` —
      // are preserved across the wipe. Without this, the right-click
      // "Add Comment" flow loses its newly-created row when the panel
      // mounts and fires its own fetch a tick later, because the server
      // hasn't seen the local comment yet.
      let seed: { [commentId: string]: DocumentComment };
      if (offset) {
        seed = { ...state.documentComments };
      } else {
        seed = {};
        for (const [id, c] of Object.entries(state.documentComments)) {
          if (!c.done) {
            seed[id] = c;
          }
        }
      }
      const next: { [commentId: string]: DocumentComment } = seed;
      for (const { data } of records) {
        next[data.commentId] = data;
      }
      state.documentComments = next;
      state.isFetchingDocumentComments = false;
    })
    .addCase(actions.FETCH_DOCUMENT_COMMENTS_FAILURE, (state) => {
      state.isFetchingDocumentComments = false;
    })
    .addCase(actions.ADD_NEW_DOCUMENT_COMMENT, (state, action) => {
      const {
        commentId,
        tdoId,
        userId,
        searchName,
        page,
        x,
        y,
        width,
        height,
      } = action.payload;
      state.documentComments[commentId] = {
        id: commentId,
        commentId,
        tdoId,
        createdBy: userId,
        searchName,
        comment: '',
        done: false,
        read: false,
        deleted: false,
        archived: false,
        createdDateTime: '',
        modifiedDateTime: '',
        // Anchor placeholders — `undefined` here means "document-level".
        // The anchored-comments UI (VE-21982 follow-up) will populate them.
        page,
        x,
        y,
        width,
        height,
        isCancelledNewComment: true,
      };
    })
    .addCase(actions.UPDATE_DOCUMENT_COMMENT_SUCCESS, (state, action) => {
      const {
        createStructuredData: { data },
        isUndo,
      } = action.payload;
      state.documentComments[data.commentId] = data;
      if (
        !isUndo &&
        state.documentCommentDrafts[data.commentId] === data.comment
      ) {
        delete state.documentCommentDrafts[data.commentId];
      }
    })
    .addCase(actions.REMOVE_DOCUMENT_COMMENT, (state, action) => {
      delete state.documentComments[action.payload.commentId];
      delete state.documentCommentDrafts[action.payload.commentId];
    })
    .addCase(actions.SELECT_DOCUMENT_COMMENT, (state, action) => {
      const { commentId } = action.payload;
      const selected = state.selectedDocumentComments;
      const idx = selected.indexOf(commentId);
      if (idx > -1) {
        state.selectedDocumentComments = [
          ...selected.slice(0, idx),
          ...selected.slice(idx + 1),
        ];
      } else {
        state.selectedDocumentComments = [...selected, commentId];
      }
    })
    .addCase(actions.SET_EDITING_DOCUMENT_COMMENT, (state, action) => {
      state.editingDocumentCommentId = action.payload.commentId;
    })
    .addCase(actions.SET_DOCUMENT_COMMENT_DRAFT, (state, action) => {
      const { commentId, comment } = action.payload;
      state.documentCommentDrafts[commentId] = comment;
    })
    .addCase(actions.CLEAR_DOCUMENT_COMMENT_DRAFT, (state, action) => {
      delete state.documentCommentDrafts[action.payload.commentId];
    })
    .addCase(actions.FOCUS_DOCUMENT_COMMENT, (state, action) => {
      state.focusedDocumentCommentId = action.payload.commentId;
    })
    /**
     * HAND THE TAB BACK once the reviewer starts producing work.
     *
     * The editor put them on Detections because there was nothing on
     * Redactions to come back to. The moment they draw, select or keyword a
     * redaction, that stops being true — and the tab they are looking at is
     * no longer the one their work is landing in.
     *
     * A matcher rather than a case per action because the LIST is the point:
     * it has to be read as one rule, and an action added to the redaction
     * family later should be added here in the same breath.
     *
     * Runs after the cases above, so the redaction is already in state.
     */
    .addMatcher(
      (action): action is { type: string } =>
        PRODUCES_REDACTION_WORK.has((action as { type: string }).type),
      (state) => {
        if (!state.session?.autoLandedOnDetections) {
          return;
        }
        state.session.redactionsSubtab = 'all';
        state.session.autoLandedOnDetections = false;
      }
    )
    /**
     * ...and stop offering, once the reviewer has touched the panel.
     *
     * Past that point they have chosen what they are looking at, and the
     * editor moving it underneath them is worse than a stale tab — this is
     * the difference between a helpful default and a control that fights you.
     *
     * The set is deliberately broad: reading counts. Opening a filter,
     * searching, expanding a card or running the engine are all someone
     * working in this panel, not just an explicit tab click.
     */
    .addMatcher(
      (action): action is { type: string } =>
        ENDS_PANEL_HANDOVER.has((action as { type: string }).type),
      (state) => {
        if (state.session) {
          state.session.autoLandedOnDetections = false;
        }
      }
    );

  // Keyword "Redact" / "Redact all" create their redactions via the
  // DOM-driven walker `redactPhraseInDom` (in DocumentEditor), which dispatches
  // CREATE_MANUAL_REDACTION(S) — no dedicated reducer case here on purpose.
});

// ---------------------------------------------------------------------------
// Local helpers used by the reducer
// ---------------------------------------------------------------------------

function pushSnapshot(
  session: SessionState,
  overrides?: Partial<UndoSnapshot>
): void {
  const snap = { ...snapshotFromSession(current(session)), ...overrides };
  session.undoStack = pushUndoSnapshot(session.undoStack, snap);
}

/**
 * Restores a popped snapshot onto the session. `sessionAppliedLexiconIds`
 * lives under `session.lexiconApply`, not on the session itself, so a plain
 * `Object.assign(session, popped)` would leave the live re-evaluation set
 * untouched (and add a stray top-level key) — which is exactly the bug the
 * field exists to fix (review-gate finding 4).
 */
function applyUndoSnapshot(session: SessionState, popped: UndoSnapshot): void {
  const { sessionAppliedLexiconIds, lexiconApplyRequestId, ...sessionFields } =
    popped;
  Object.assign(session, sessionFields);
  session.lexiconApply.sessionAppliedLexiconIds = [...sessionAppliedLexiconIds];

  // A SETTLED run's summary describes the detections that run produced, so
  // undoing THAT run's own step must retire it — otherwise Ctrl+Z after an
  // apply left the panel reading "15 matches found." over a list with no
  // lexicon rows, which is indistinguishable from lost results.
  //
  // The test is PRESENCE of the mark, not equality with the live request id.
  // Only an apply's own snapshot carries `lexiconApplyRequestId`, so presence
  // already means "this step was an apply" — which is what stops an unrelated
  // undo from wiping the summary (review-gate R5).
  //
  // Equality was the third wrong design here and reproduced the original bug
  // in the ORDINARY path. `LEXICON_APPLY_STARTED` advances `requestId` on every
  // page-text re-evaluation, while only a run that CHANGED something pushes a
  // snapshot — so after one no-op re-evaluation the popped id no longer matched
  // the live one, the reset was skipped, and the panel read "1 match found."
  // over a list with no lexicon rows. Re-evaluation fires on every text burst
  // and most bursts add nothing, so that was the common case, not a corner.
  //
  // Presence is sufficient because a snapshot that predates run X predates
  // every run after X: whatever summary is on screen cannot describe detections
  // this snapshot was taken before (review-gate #3 BR-7).
  //
  // In-flight runs are left alone: their SUCCESS re-reads the session and
  // merges against whatever undo restored.
  const apply = session.lexiconApply;
  if (
    (apply.status === 'done' || apply.status === 'error') &&
    lexiconApplyRequestId !== undefined
  ) {
    session.lexiconApply = {
      ...emptyLexiconApplyState(),
      sessionAppliedLexiconIds: [...sessionAppliedLexiconIds],
    };
  }
}

/**
 * Merges newly applied lexicon records over the existing ones, keyed by
 * `lexiconId`.
 *
 * Re-applying replaces the record rather than appending a second one: the panel
 * shows "which revision is applied", and two records for one lexicon would make
 * that question unanswerable. The applied ORDER of first application is kept so
 * the list does not reshuffle under the reviewer on every re-apply.
 */
function mergeAppliedLexiconRecords(
  existing: readonly AppliedLexiconRecord[],
  incoming: readonly AppliedLexiconRecord[]
): AppliedLexiconRecord[] {
  const byId = new Map(incoming.map((r) => [r.lexiconId, r]));
  const out = existing.map((r) => byId.get(r.lexiconId) ?? r);
  const seen = new Set(existing.map((r) => r.lexiconId));
  for (const r of incoming) {
    if (!seen.has(r.lexiconId)) {
      out.push(r);
    }
  }
  return out;
}

/**
 * Drop a lexicon card that nothing is holding any more, once its lexicon has
 * been taken off the document.
 *
 * Removing a lexicon keeps its REDACTED matches — they are the reviewer's work
 * rather than the lexicon's suggestion, and their cards carry the provenance of
 * a redaction whose lexicon is gone. That is the only reason those cards
 * outlive the removal. When the last of those redactions is taken back, the
 * reason goes with it: the card is not in the Redactions list, and it is not in
 * the Detections list either, because the Categories dropdown has no row for a
 * lexicon this document is no longer searching. It would otherwise sit in the
 * session unseen and be written into every saved asset from then on.
 *
 * Deliberately narrow. A card still promoted to Redactions stays, whatever its
 * lexicon's status; so does any card whose lexicon IS applied, which is an
 * ordinary un-redacted suggestion. Only the intersection — no longer applied,
 * nothing redacted, not promoted — is dead.
 */
function dropOrphanedLexiconDetections(session: SessionState): void {
  const applied = new Set(
    session.appliedLexicons.map((record) => record.lexiconId)
  );
  const isOrphan = (d: Detection) =>
    isLexiconDetection(d) &&
    !applied.has(d.lexicon.lexiconId) &&
    !d.addedToRedactions &&
    (d.redactedMatchIds?.length ?? 0) === 0;

  const doomed = current(session.detections).filter(isOrphan);
  if (doomed.length === 0) {
    return;
  }
  const ids = new Set(doomed.map((d) => d.id));
  session.detections = current(session.detections).filter(
    (d) => !ids.has(d.id)
  );
  // Overrides are keyed `${detectionId}|${rectIndex}`, so a dropped card's
  // nudged boxes go with it — the same cleanup `REMOVE_LEXICON_FROM_DOCUMENT`
  // does, and for the same reason: an orphaned key can later land on a
  // different match's box.
  const overrides = { ...current(session.detectionRectOverrides) };
  for (const key of Object.keys(overrides)) {
    if ([...ids].some((id) => key.startsWith(`${id}|`))) {
      delete overrides[key];
    }
  }
  session.detectionRectOverrides = overrides;
}

/**
 * Retire a settled run summary, keeping what must outlive it.
 *
 * "17 matches found." describes a set of detections. Once something has taken
 * part of that set off the document the sentence is no longer true of what the
 * panel is showing, and leaving it up is indistinguishable from lost results —
 * the reading `applyUndoSnapshot` already guards against for Ctrl+Z.
 *
 * Done HERE, in the reducer, rather than by a flag in the panel. The panel is
 * mounted from more than one branch of `DetectionsPanel`, so a component-held
 * "the summary is stale" flag died on any remount — PII detections arriving and
 * turning on the source-tab strip was enough — and the retired sentence came
 * back over the shortened list without a new run. A fact about redux state
 * belongs in redux.
 *
 * An UNREAD auto-redact summary survives, because it is not a fact about the
 * detections this run found: it is the notice that matches were redacted with
 * nobody asked, and the reviewer has not seen it yet.
 */
function retireLexiconRunSummary(session: SessionState): void {
  const previous = session.lexiconApply;
  if (previous.status !== 'done' && previous.status !== 'error') {
    return;
  }
  session.lexiconApply = {
    ...emptyLexiconApplyState(),
    sessionAppliedLexiconIds: [...previous.sessionAppliedLexiconIds],
    autoRedact: previous.autoRedact.acknowledged
      ? emptyLexiconApplyState().autoRedact
      : { ...previous.autoRedact, running: false },
  };
}

/** A session's lexicon-apply state at open: nothing running, nothing applied yet. */
function emptyLexiconApplyState(): LexiconApplyState {
  return {
    status: 'idle',
    requestId: null,
    pagesMatched: [],
    matchCount: 0,
    abandonedEntryIds: [],
    abandonedLexiconNames: [],
    timedOut: false,
    crashed: false,
    unsearchedPages: [],
    sessionAppliedLexiconIds: [],
    autoRedact: {
      running: false,
      cancelled: false,
      redactedCount: 0,
      redactedMatchIds: [],
      acknowledged: true,
    },
  };
}

function markDirty(session: SessionState): void {
  // Advance rev even while `saving`, so SAVE_SUCCESS can detect a mid-save edit.
  session.save.rev += 1;
  if (session.save.status !== 'saving') {
    session.save.status = 'dirty';
  }
}

export default reducer;
// Build the auto-save payload selector dependency in selectors.ts via this helper.
export { buildPersistedRedactionState };
// keep ALL_PII_TYPES re-exported for convenience
export { ALL_PII_TYPES };
